/**
 * Dutch (Belgium). Terms follow the AREI and what Flemish installers say on site: eendraadschema,
 * situatieschema, kring, automaat, differentieelschakelaar, stopcontact, lichtpunt, bord.
 * The regulation is "AREI" in Dutch (RGIE in French).
 */
import type { Catalogue } from './fr.ts';

export const nl: Catalogue = {
  'app.name': 'Tablo',
  'app.tagline': 'Elektrische schema’s voor installateurs',

  'menu.file': 'Bestand',
  'menu.new': 'Nieuw',
  'menu.open': 'Openen…',
  'menu.save': 'Opslaan',
  'menu.saveAs': 'Opslaan als…',
  'menu.export': 'Exporteren',
  'menu.exportPdf': 'PDF (alle bladen)',
  'menu.exportSvg': 'SVG (huidig blad)',
  'menu.exportPng': 'PNG (huidig blad)',
  'menu.print': 'Afdrukken…',
  'menu.projectInfo': 'Projectgegevens',
  'menu.demo': 'Voorbeeld',
  'menu.benchmark': 'Prestatietest',
  'menu.undo': 'Ongedaan maken',
  'menu.redo': 'Opnieuw',
  'menu.fit': 'Alles tonen',
  'menu.language': 'Taal',
  'menu.copy': 'Kopiëren',
  'menu.cut': 'Knippen',
  'menu.paste': 'Plakken',
  'menu.duplicate': 'Dupliceren',
  'menu.selectAll': 'Alles selecteren',

  'tool.select': 'Selecteren',
  'tool.wire': 'Draad',
  'tool.text': 'Tekst',
  'tool.rotate': 'Draaien',
  'tool.mirror': 'Spiegelen',
  'tool.delete': 'Verwijderen',

  'sheet.add': 'Nieuw blad',
  'sheet.rename': 'Naam wijzigen',
  'sheet.delete': 'Blad verwijderen',
  'sheet.confirmDelete': 'Het blad “{name}” en alles wat erop staat, wordt verwijderd.',
  'sheet.lastOne': 'Een project moet minstens één blad houden.',
  'sheetKind.single-line': 'Eendraadschema',
  'sheetKind.multi-line': 'Meerdraadsschema',
  'sheetKind.control': 'Stuurschema',
  'sheetKind.position-plan': 'Situatieschema',
  'sheetKind.board': 'Bordindeling',

  'status.elements': '{n} elementen',
  'status.wires': '{n} draden',
  'status.zoom': 'Zoom {pct}%',
  'status.platform.desktop': 'Desktop',
  'status.platform.mobile': 'Mobiel',
  'status.platform.web': 'Web',
  'status.saved': 'Opgeslagen',
  'status.unsaved': 'Niet-opgeslagen wijzigingen',
  'status.autosaved': 'Reservekopie om {time}',
  'status.exported': 'Geëxporteerd: {name}',

  'panel.selection': 'Selectie',
  'panel.noSelection': 'Niets geselecteerd',
  'panel.multi': '{n} elementen geselecteerd',
  'panel.tag': 'Referentie',
  'panel.tagDuplicate': 'Deze referentie wordt al gebruikt',
  'panel.symbol': 'Symbool',
  'panel.position': 'Positie (mm)',
  'panel.rotation': 'Draaiing',
  'panel.mirrored': 'Gespiegeld',
  'panel.wire': 'Draad',
  'panel.wireLabel': 'Label / kabel',
  'panel.wireLabelHint': 'bv. XVB 3G2,5',
  'panel.sheet': 'Blad',
  'panel.sheetName': 'Naam',
  'panel.sheetKind': 'Soort schema',
  'panel.paper': 'Formaat',
  'panel.orientation': 'Richting',
  'panel.landscape': 'Liggend',
  'panel.portrait': 'Staand',
  'panel.symbols': 'Symbolen',
  'panel.search': 'Symbolen zoeken…',
  'panel.noResults': 'Geen symbolen gevonden',
  'panel.allSheets': 'Alle symbolen',

  'cat.protection': 'Beveiliging',
  'cat.distribution': 'Verdeling',
  'cat.outlet': 'Stopcontacten',
  'cat.appliance': 'Toestellen',
  'cat.lighting': 'Verlichting',
  'cat.command': 'Bediening',
  'cat.control': 'Sturing',
  'cat.motor': 'Motoren',
  'cat.signalling': 'Signalisatie',
  'cat.connection': 'Aansluiting',
  'cat.earth': 'Aarding',
  'cat.annotation': 'Annotatie',

  'meta.title': 'Projectnaam',
  'meta.ownerName': 'Eigenaar',
  'meta.ownerAddress': 'Adres van de eigenaar',
  'meta.installationAddress': 'Adres van de installatie (indien anders)',
  'meta.author': 'Getekend door',
  'meta.company': 'Firma',
  'meta.date': 'Datum',
  'meta.earthingSystem': 'Aardingsstelsel',
  'meta.supplyVoltage': 'Voedingsspanning',
  'meta.locale': 'Taal van de schema’s',
  'meta.standard': 'Norm',
  'meta.standardHint': 'Regels die de controle van het project volgt.',
  'std.rgie': 'AREI',
  'std.nfc15100': 'NF C 15-100',
  'std.nen1010': 'NEN 1010',
  'std.label.rgie': 'AREI (België)',
  'std.label.nfc15100': 'NF C 15-100 (Frankrijk)',
  'std.label.nen1010': 'NEN 1010 (Nederland)',
  'meta.hint': 'Deze gegevens staan in het titelvak van elk blad.',

  'tb.project': 'Project',
  'tb.sheet': 'Blad',
  'tb.sheetNo': 'Nr.',
  'tb.owner': 'Eigenaar',
  'tb.date': 'Datum',
  'tb.paper': 'Formaat',
  'tb.installation': 'Adres van de installatie',
  'tb.supply': 'Spanning',
  'tb.earthing': 'Aarding',
  'tb.author': 'Getekend door / firma',
  'tb.diagram': 'Soort schema',

  'dialog.ok': 'OK',
  'dialog.cancel': 'Annuleren',
  'dialog.close': 'Sluiten',
  'dialog.delete': 'Verwijderen',
  'dialog.discard': 'Wijzigingen verwerpen',
  'confirm.discard': 'Niet-opgeslagen wijzigingen gaan verloren.',
  'confirm.discardTitle': 'Wijzigingen verwerpen?',

  'recovery.title': 'Niet-opgeslagen werk herstellen?',
  'recovery.body': '“{name}” werd gewijzigd op {date} en niet opgeslagen.',
  'recovery.restore': 'Herstellen',
  'recovery.dismiss': 'Negeren',
  'recovery.delete': 'Verwijderen',
  'recovery.fromAccount': '(kopie uit uw account)',
  'recovery.pick': 'Te herstellen kopie',

  'bench.title': 'Prestatietest',
  'bench.running': 'Meten… ({n} elementen)',
  'bench.result': '{n} elementen · {fps} fps · gem. beeld {avg} ms · p95 {p95} ms',

  'error.open': 'Kon het bestand niet openen: {msg}',
  'error.save': 'Kon niet opslaan: {msg}',
  'error.export': 'Kon niet exporteren: {msg}',

  'hint.canvas':
    'Slepen: selecteren · Wieltje: zoomen · Middelste knop, spatie + slepen of twee vingers: verschuiven',
  'hint.place': 'Klik: plaatsen · R: draaien · Esc: klaar',
  'hint.wire':
    'Klik op een klem om te beginnen · Klik: hoek · Dubbelklik: einde · Backspace: hoek terug · Esc: annuleren',

  'tx.move': 'Verplaatsen',
  'tx.add': '{name} toevoegen',
  'tx.delete': 'Verwijderen',
  'tx.rotate': 'Draaien',
  'tx.mirror': 'Spiegelen',
  'tx.wire': 'Draad tekenen',
  'tx.paste': 'Plakken',
  'tx.edit': 'Wijzigen',
  'tx.sheetAdd': 'Blad toevoegen',
  'tx.sheetDelete': 'Blad verwijderen',
  'tx.meta': 'Projectgegevens',
  'tool.room': 'Ruimte',
  'tool.calibrate': 'Schaal instellen',
  'panel.circuits': 'Kringen',
  'panel.checks': '{std}-controle',
  'panel.hide': 'Paneel verbergen',
  'panel.background': 'Achtergrondplan',
  'circ.code': 'Kring',
  'circ.label': 'Omschrijving',
  'circ.protection': 'Beveiliging',
  'circ.rcd': 'Differentieel',
  'circ.cableType': 'Kabel',
  'circ.section': 'mm²',
  'circ.cores': 'Aders',
  'circ.length': 'Lengte (m)',
  'circ.points': 'Punten',
  'circ.legacy': 'Oud gedeelte',
  'circ.add': 'Kring toevoegen',
  'circ.renumber': 'Kringen nummeren',
  'circ.empty':
    'Geen kringen: plaats een automaat op een eendraadschema, importeer een foto van het bord of vraag het aan de AI-assistent.',
  'circ.mainBus': 'Hoofdrail',
  'circ.kindSockets': 'Stopcontacten',
  'circ.kindLights': 'Verlichting',
  'circ.kindAppliance': 'Toestel',
  'circ.newLabel': 'Nieuwe kring',
  'circ.show': 'Tonen op het schema',
  'circ.noSingleLine': 'Voeg eerst een blad “Eendraadschema” toe.',
  'checks.ok': 'Geen problemen gevonden met de huidige regels.',
  'checks.summary': '{e} fout(en), {w} waarschuwing(en), {i} info',
  'checks.unverified': 'regel te bevestigen',
  'checks.why': 'Waarom',
  'checks.fix': 'Oplossing',
  'checks.askAssistant': 'Oplossen met de assistent',
  'checks.goProjectInfo': 'Projectgegevens invullen',
  'checks.goMeasurements': 'Meetresultaten invullen',
  'checks.goCircuits': 'Kringtabel openen',
  'checks.goProperties': 'Eigenschappen bewerken',
  'checks.goSheet': 'Bladinstellingen',
  'checks.goShow': 'Tonen op de tekening',
  'checks.goNewPlan': 'Leeg situatieschema maken',
  'checks.goAddEarth': 'Aardverbinding toevoegen',
  'checks.goQuick': 'Oplossen',
  'checks.quickMove30': 'Onder {tag} plaatsen (30 mA)',
  'checks.quickTypeA': '{tag} naar type A',
  'checks.quickMain300': '{tag} op 300 mA zetten',
  'checks.quickRating': 'Verlagen naar {tag}',
  'checks.quickSection': 'Kabel {tag} (herbekabeling)',
  'circ.estimated': 'Geschat uit het plan: ter plaatse controleren',
  'checks.goRenumber': 'Kringen nummeren',
  'checks.goNumberTerminals': 'Klemmen nummeren',
  'checks.goA3': 'Blad naar A3',
  'checks.goEarthSockets': 'Vervangen door geaarde stopcontacten',
  'checks.goRename': '{from} hernoemen naar {tag}',
  'checks.goAttach': 'Aansluiten op kring…',
  'checks.goDrawRoom': 'Er een ruimte rond tekenen',
  'checks.goDelete': 'Verwijderen',
  'checks.goSpare': 'Als reserve markeren',
  'checks.goEstimate': 'Schatten uit het plan',
  'checks.goPhoto': 'Lezen van een foto',
  'checks.estimated': 'Lengtes geschat uit het plan voor {codes}: ter plaatse controleren.',
  'checks.estimateNone': 'Geen kring meetbaar op het plan (bord en punten nodig op een geïmporteerd plan).',
  'checks.photoRead': 'Gelezen op de foto: {what}. Controleer het.',
  'checks.photoNone': 'Niets leesbaar op de foto.',
  'circ.spare': 'Reserve',
  'measure.again': 'Opnieuw meten',
  'measure.previous': 'Eerder: {what} ({date})',
  'checks.attached': '{tag} aangesloten op kring {code}.',
  'checks.disclaimer':
    'Helpt de keuring voor te bereiden — vervangt het onderzoek door een erkend organisme niet.',
  'status.checks': '{std}: {e} fout(en) · {w} waarsch.',
  'plan.import': 'Plan importeren (afbeelding, PDF, DXF of ruimtescan)…',
  'plan.replace': 'Plan vervangen…',
  'plan.remove': 'Achtergrond verwijderen',
  'plan.opacity': 'Dekking',
  'plan.scale': 'Schaal 1:',
  'plan.calibrate': 'Schaal instellen…',
  'plan.calibrateHint': 'Klik op twee punten waarvan je de echte afstand kent (bv. een muur).',
  'plan.distance': 'Echte afstand tussen de twee punten (m)',
  'plan.measured': 'Gemeten op het blad: {mm} mm',
  'plan.toPlace': 'Te plaatsen op het plan',
  'plan.allPlaced': 'Alle punten van het schema zijn geplaatst.',
  'plan.importing': 'Plan importeren…',
  'plan.noBackground': 'Geen achtergrondplan.',
  'ai.menu': 'AI',
  'ai.board': 'Foto van het bord importeren…',
  'ai.demoBanner':
    'Demomodus: de AI is nog niet verbonden. Het resultaat hieronder is een voorbeeld; de foto wordt niet geanalyseerd.',
  'ai.demoText': 'Demomodus: vereenvoudigde lokale analyse (zonder AI).',

  // Sign-in prompt for the AI features (signed out).
  'aiGate.title': 'AI-functies (bèta) met een gratis Tablo-account',
  'aiGate.intro':
    'De AI-functies zijn gratis tijdens de bèta; u hoeft alleen aangemeld te zijn. Dit doen ze voor u:',
  'aiGate.tab.assist': 'Assistent',
  'aiGate.tab.board': 'Foto van het bord',
  'aiGate.tab.sketch': 'Schets',
  'aiGate.tab.report': 'Keuringsverslag',
  'aiGate.cap.assist':
    'Vraag een wijziging in gewone taal: de assistent bereidt ze voor, u past ze toe met één klik.',
  'aiGate.cap.board':
    'Fotografeer een bestaand bord: de verliesstroomschakelaars en kringen worden voor u uitgelezen.',
  'aiGate.cap.sketch': 'Een met de hand getekend eendraadsschema wordt een net, bewerkbaar schema.',
  'aiGate.cap.report':
    'Importeer het verslag van een erkend organisme: de opmerkingen worden overgenomen, klaar om op te volgen.',
  'aiGate.signIn': 'Aanmelden of account aanmaken',
  'aiGate.later': 'Later',
  'ai.choosePhoto': 'Foto kiezen…',
  'ai.analyse': 'Analyseren',
  'ai.analysing': 'Bezig met analyseren…',
  'ai.preview': 'Voorbeeld',
  'ai.previewHint': 'Beweeg over het voorstel om het op het schema te zien; klik om het voorbeeld te houden.',
  'ai.previewBanner':
    'Voorbeeld van het voorstel, nog niet toegepast: {added} toegevoegd, {changed} gewijzigd, {removed} verwijderd',
  'ai.rechecking': 'Rail van dichtbij opnieuw lezen…',
  'ai.recheckNote': 'Rail {rail} van dichtbij opnieuw gelezen: {labels} gecorrigeerd.',
  'ai.review': 'Controleer elke rij vóór het importeren. Onzekere waarden zijn gemarkeerd.',
  'ai.row': 'Rail',
  'ai.pos': 'Pos.',
  'ai.type': 'Toestel',
  'ai.rating': 'A',
  'ai.curve': 'Curve',
  'ai.sens': 'mA',
  'ai.rcdType': 'Type',
  'ai.label': 'Label',
  'ai.confidence': 'Zekerheid',
  'ai.import': 'Eendraadschema maken',
  'ai.notes': 'Opmerkingen',
  'ai.target': 'Toevoegen onder',
  'ai.error': 'AI-fout: {msg}',
  'ai.unavailable':
    'De AI-functies (bèta) zijn tijdelijk niet beschikbaar. Probeer het later opnieuw: de rest van Tablo werkt gewoon.',
  'ai.removeRow': 'Rij verwijderen',
  'ai.addRow': 'Rij toevoegen',
  'ai.dt.main-switch': 'Hoofdschakelaar',
  'ai.dt.RCD': 'Differentieel',
  'ai.dt.MCB': 'Automaat',
  'ai.dt.RCBO': 'Differentieelautomaat',
  'ai.dt.fuse': 'Smeltveiligheid',
  'ai.dt.contactor': 'Contactor',
  'ai.dt.timer': 'Tijdschakelaar',
  'ai.dt.meter': 'Meter',
  'ai.dt.other': 'Andere',
  'ai.toFill': 'Punten en kabels nog in te vullen: een foto toont ze niet.',
  'menu.reports': 'Rapporten',
  'menu.circuitsCsv': 'Kringenlijst (CSV)',
  'menu.bomCsv': 'Materiaallijst (CSV)',
  'menu.labelsPdf': 'Etiketten voor het bord (PDF)',
  'menu.exportPdfFull': 'PDF + kringenlijst',
  'tx.calibrate': 'Schaal instellen',
  'tx.background': 'Achtergrondplan',
  'tx.circuit': 'Kring wijzigen',
  'tx.addCircuit': 'Kring toevoegen',
  'tx.renumber': 'Kringen nummeren',
  'tx.aiImport': 'Bord importeren (AI)',
  'room.default': 'Ruimte',
  'hint.room': 'Sleep om een ruimte te tekenen · Esc: klaar',
  'hint.calibrate': 'Klik op het eerste punt, dan op het tweede · Esc: annuleren',
  'menu.diagram': 'Schema',
  'menu.newFromTemplate': 'Nieuw vanuit een sjabloon…',
  'menu.terminalPlanCsv': 'Klemmenplan (CSV)',
  'cmd.multiLine': 'Meerdraadsschema maken / bijwerken',
  'cmd.numberWires': 'Draden op dit blad nummeren',
  'cmd.numberTerminals': 'Klemmen nummeren',
  'cmd.insert': 'Invoegen',
  'cmd.nothing': 'Niets te wijzigen',
  'cmd.multiLineDone': 'Meerdraadsschema “{name}” is bijgewerkt',
  'tpl.title': 'Nieuw project vanuit een sjabloon',
  'tpl.create': 'Maken',
  'ml.generated':
    'Blad gemaakt vanuit “{name}”: wat je hier met de hand wijzigt, gaat verloren bij het bijwerken.',
  'ml.stale': 'Het eendraadschema “{name}” is gewijzigd: dit gemaakte blad is niet meer actueel.',
  'ml.update': 'Bijwerken',
  'tx.multiLine': 'Meerdraadsschema',
  'tx.numberWires': 'Draden nummeren',
  'tx.numberTerminals': 'Klemmen nummeren',
  'tx.template': 'Invoegen: {name}',
  'tx.assistant': 'AI-assistent',
  'tx.sketch': 'Schets importeren',
  'ai.assistant': 'Assistent',
  'ai.sketch': 'Schets → schema…',
  'ai.send': 'Versturen',
  'ai.askHint': 'Bv. “voeg een laadpaal toe en maak dan het meerdraadsschema”',
  'ai.thinking': 'Even nadenken…',
  'ai.dictate': 'Dicteren',
  'ai.stopDictation': 'Dictee stoppen ({s} s)',
  'ai.transcribing': 'Uitschrijven…',
  'ai.micDenied':
    'Toegang tot de microfoon geweigerd: sta die toe in de instellingen van de browser of het toestel.',
  'ai.micMissing': 'Geen microfoon gevonden.',
  'ai.nothingHeard': 'Niets gehoord: spreek dichter bij de microfoon.',
  'ai.apply': 'Toepassen',
  'ai.applied': 'Toegepast (ongedaan maken met {mod}Z)',
  'ai.fbUp': 'Nuttig antwoord',
  'ai.fbDown': 'Fout of nutteloos antwoord',
  'ai.fbWhat': 'Wat ging er mis? (optioneel)',
  'ai.fbShare':
    'Dit gesprek en het project (zonder namen, adressen of foto’s) delen zodat het een testgeval kan worden',
  'ai.fbTrain': 'Het ook gebruiken om het eigen AI-model van Tablo te trainen',
  'ai.fbShareHint':
    'Hoogstens 12 maanden bewaard, alleen door Tablo. Een model dat er al mee getraind is, wordt na het wissen niet opnieuw getraind. Zie het privacybeleid.',
  'ai.fbShareGood': 'Dit antwoord delen als goed voorbeeld?',
  'ai.fbSend': 'Versturen',
  'ai.fbCancel': 'Annuleren',
  'ai.fbThanks': 'Bedankt voor uw feedback.',
  'ai.fbError': 'Feedback niet verstuurd: {msg}',
  'ai.skipped': 'Niet toegepast: {msg}',
  'ai.proposal': 'Voorgestelde wijzigingen',
  'ai.chooseSketch': 'Afbeelding kiezen…',
  'ai.sketchHint': 'Foto of scan van een met de hand getekend eendraadschema.',
  'ai.sketchResult': '{n} kring(en) gelezen, in {g} groep(en).',
  'ai.mainRcd': 'Hoofddifferentieel',
  'circ.method': 'Plaatsing',
  'circ.method.A1': 'A1 · buis in thermisch geïsoleerde wand',
  'circ.method.B1': 'B1 · in buis (in of op een wand)',
  'circ.method.C': 'C · kabel op een wand bevestigd',
  'circ.ib': 'Ib (A)',
  'circ.iz': 'Iz (A)',
  'circ.drop': 'ΔU',
  'circ.lmax': 'Max. L',
  'circ.ibLoads': 'Berekend uit het vermogen van de punten',
  'circ.ibRating': 'Vermogen onbekend: gecontroleerd op de nominale stroom van de beveiliging',
  'circ.dropLimit': 'Gebruikelijke grens: {pct} %',
  'circ.lmaxHint': 'Maximale lengte beveiligd tegen kortsluiting (conventionele methode)',
  'info.icc': 'Verwachte kortsluitstroom aan de oorsprong (kA)',
  'info.iccHint': 'Gegeven door de netbeheerder; dient om het onderbrekingsvermogen te controleren.',
  'menu.exportDxf': 'DXF (huidig blad, voor AutoCAD/QCAD…)',
  'tb.paperRev': 'Formaat · ind.',
  'rev.menu': 'Revisies…',
  'rev.title': 'Revisies',
  'rev.index': 'Ind.',
  'rev.date': 'Datum',
  'rev.author': 'Door',
  'rev.description': 'Wijziging',
  'rev.new': 'Nieuwe revisie',
  'rev.newHint': 'Legt het huidige project vast als index {index}.',
  'rev.create': 'Index {index} maken',
  'rev.none':
    'Nog geen revisies. Maak index A wanneer je het schema overhandigt (aan de klant, het keuringsorganisme…).',
  'rev.compare': 'Vergelijken',
  'rev.restore': 'Terug naar deze toestand',
  'rev.restoreConfirm': 'Het project vervangen door de toestand van index {index}? (Kan ongedaan worden.)',
  'rev.diff': 'Vergeleken met index {index}: {added} toegevoegd, {changed} gewijzigd, {removed} verwijderd',
  'rev.diffClose': 'Vergelijking beëindigen',
  'tx.revision': 'Index {index}',
  'tx.restoreRevision': 'Terug naar index {index}',
  'rev.table': 'Revisies',
  'menu.revisionsPdf': 'Revisietabel (in de volledige PDF)',
  'cat.user': 'Mijn symbolen',
  'sym.new': 'Nieuw symbool…',
  'sym.edit': 'Symbool wijzigen',
  'sym.newTitle': 'Nieuw symbool',
  'sym.editTitle': 'Symbool wijzigen',
  'sym.save': 'Symbool opslaan',
  'sym.nameFr': 'Naam (Frans)',
  'sym.nameEn': 'Naam (Engels)',
  'sym.nameNl': 'Naam (Nederlands)',
  'sym.tagPrefix': 'Letter van de referentie',
  'sym.width': 'Breedte (mm)',
  'sym.height': 'Hoogte (mm)',
  'sym.views': 'Beschikbaar in',
  'sym.hint':
    'Raster van 1,25 mm. Sleep om te tekenen; “Aansluitpunt” voegt een punt toe (of verwijdert het) waar draden aansluiten.',
  'sym.tool.line': 'Lijn',
  'sym.tool.rect': 'Rechthoek',
  'sym.tool.circle': 'Cirkel',
  'sym.tool.dot': 'Schijf',
  'sym.tool.text': 'Tekst',
  'sym.tool.port': 'Aansluitpunt',
  'sym.tool.erase': 'Wissen',
  'sym.problems': 'Nog aan te vullen: {list}',
  'sym.problem.name': 'naam',
  'sym.problem.empty': 'tekening',
  'sym.problem.views': 'soorten schema',
  'sym.problem.port': 'dubbele punten',
  'sym.problem.port-outside': 'punt buiten het kader',
  'tx.newSymbol': 'Nieuw symbool: {name}',
  'tx.editSymbol': 'Symbool gewijzigd: {name}',
  'tx.importSymbols': 'Symbolen importeren',
  'macro.title': 'Macro’s',
  'macro.none': 'Selecteer een groep elementen, dan “Schema → Selectie opslaan als macro”.',
  'macro.save': 'Selectie opslaan als macro…',
  'macro.name': 'Naam van de macro',
  'macro.saved': 'Macro “{name}” opgeslagen',
  'macro.selectFirst': 'Selecteer eerst de elementen om op te slaan',
  'macro.insert': 'Invoegen in het midden van het beeld',
  'macro.delete': 'Macro verwijderen',
  'macro.deleteConfirm': 'De macro “{name}” uit je bibliotheek verwijderen?',
  'lib.import': 'Importeren…',
  'lib.exportSymbols': 'Mijn symbolen exporteren',
  'lib.exportMacros': 'Mijn macro’s exporteren',
  'lib.imported': '{n} element(en) geïmporteerd',
  'lib.notLibrary': 'Dit bestand is geen Tablo-bibliotheek',
  'lib.symbolsFile': 'Tablo-symbolen',
  'lib.macrosFile': 'Tablo-macro’s',
  'cat.ref': 'Fabrikantreferentie',
  'cat.none': '— geen —',
  'cat.noMatch': 'Geen catalogusreferentie past',
  'cat.modules': '{n} module(s)',
  'cat.source': 'bron',
  'cat.mismatch': 'past niet meer bij de kenmerken',
  'cat.assign': 'Catalogusreferenties',
  'cat.assigned': '{n} {brand}-referentie(s) toegekend; {missing} toestel(len) zonder overeenkomst',
  'tx.assignBrand': 'Referenties {brand}',
  'cmd.boardLayout': 'Bordindeling maken / bijwerken',
  'tx.boardLayout': 'Bordindeling',
  'legal.privacy': 'Privacybeleid',
  'legal.support': 'Support',
  'legal.registerNote': 'Uw gegevens blijven in Europa; meer in het',
  'about.menu': 'Over Tablo…',
  'about.title': 'Over',
  'about.beta':
    'Gratis bèta. Functies kunnen nog veranderen; je .tablo-bestanden blijven leesbaar voor latere versies. Meld gerust elk probleem.',
  'about.data':
    'Je projecten blijven op je computer (of in je browser): niets gaat naar een server, behalve de foto’s en teksten die je aan de AI voorlegt.',
  'about.aiDemo': 'AI: demomodus (er wordt niets verstuurd).',
  'about.aiRemote':
    'AI: de foto’s en beschrijvingen die je verstuurt, worden verwerkt en daarna verwijderd; ze dienen niet om modellen te trainen.',
  'about.rules':
    'De AREI-controle is een hulpmiddel: ze vervangt het lezen van het AREI en de keuring door een erkend organisme niet. Regels “te bevestigen” zijn aangeduid.',
  'about.checkUpdates': 'Zoeken naar updates',
  'about.update.unavailable': 'Automatische updates zijn niet beschikbaar in deze versie.',
  'about.update.up-to-date': 'Tablo is up-to-date.',
  'about.update.installed': 'Update geïnstalleerd.',
  'about.update.available': 'Update beschikbaar.',
  'about.updateAvailable': 'Update beschikbaar: {v}',
  'about.updateAvailableWord': 'beschikbaar',
  'about.install': 'Installeren en herstarten',
  'welcome.title': 'Welkom bij Tablo',
  'welcome.intro':
    'Tablo tekent de schema’s van een Belgische elektrische installatie — eendraadschema, situatieschema, meerdraadsschema, stuurschema, bordindeling — en controleert ze gaandeweg aan het AREI.',
  'welcome.s1.title': 'Het bord',
  'welcome.s1.body':
    'begin vanuit een sjabloon, fotografeer het bord (AI) of voeg kringen toe in het paneel “Kringen”.',
  'welcome.s2.title': 'De punten',
  'welcome.s2.body':
    'de stopcontacten, verlichting en toestellen van elke kring, met de kabellengtes voor de berekeningen.',
  'welcome.s3.title': 'Het situatieschema',
  'welcome.s3.body':
    'importeer het grondplan (PDF, foto of DXF), stel de schaal in en plaats de punten uit de lijst “Te plaatsen”.',
  'welcome.s4.title': 'De controle',
  'welcome.s4.body':
    '“AREI-controle” toont wat ontbreekt of niet klopt; klik op een regel om erheen te gaan.',
  'welcome.s5.title': 'De overdracht',
  'welcome.s5.body':
    'Schema → meerdraadsschema en bordindeling; Bestand → Revisies, exporteer dan de volledige PDF voor de klant en het keuringsorganisme.',
  'welcome.template': 'Beginnen vanuit een sjabloon…',
  'welcome.demo': 'Het voorbeeld openen',
  'welcome.empty': 'Leeg project',
  'cloud.menu': 'Cloud',
  'cloud.open': 'Account en online projecten…',
  'cloud.leave': 'Online project verlaten (lokale kopie behouden)',
  'cloud.title': 'Tablo online',
  'cloud.intro':
    'Met een account worden je projecten online bewaard en gesynchroniseerd tussen je toestellen; je kunt samenwerken en een link om te bekijken delen.',
  'cloud.login': 'Aanmelden',
  'cloud.register': 'Account maken',
  'cloud.name': 'Naam',
  'cloud.email': 'E-mail',
  'cloud.password': 'Wachtwoord',
  'cloud.passwordHint': 'Minstens 8 tekens.',
  'cloud.forgot': 'Wachtwoord vergeten?',
  'cloud.forgotTitle': 'Wachtwoord vergeten',
  'cloud.forgotIntro': 'Geef het adres van uw account: u krijgt een link om een nieuw wachtwoord te kiezen.',
  'cloud.forgotSend': 'Link versturen',
  'cloud.forgotSent':
    'Als er een account bestaat voor {email}, is er zonet een e-mail verstuurd. De link is één uur geldig; kijk ook bij ongewenste mail.',
  'cloud.backToLogin': 'Terug naar aanmelden',
  'cloud.registerConfirmNote': 'We sturen u een link om uw e-mailadres te bevestigen.',
  'cloud.pendingTitle': 'Bevestig uw e-mailadres',
  'cloud.pendingBody':
    'We hebben een link gestuurd naar {email}. Open hem om uw account te activeren: hij is 48 uur geldig.',
  'cloud.pendingSpam': 'Niets ontvangen? Kijk bij ongewenste e-mail, of stuur de link opnieuw.',
  'cloud.resend': 'Link opnieuw sturen',
  'cloud.resent': 'Link opnieuw verstuurd. Hij vervangt de vorige.',
  'cloud.pendingSignIn': 'Al bevestigd? Aanmelden',
  'cloud.confirmTitle': 'Account activeren',
  'cloud.confirming': 'Bezig met activeren…',
  'cloud.confirmExpired':
    'Deze link is verlopen of al gebruikt. Meld u aan: als uw adres nog niet bevestigd is, kunt u een nieuwe link krijgen.',
  'cloud.confirmRetry': 'Opnieuw proberen',
  'cloud.confirmed': 'E-mailadres bevestigd. Welkom bij Tablo!',
  'cloud.resetTitle': 'Nieuw wachtwoord',
  'cloud.resetIntro': 'Kies het nieuwe wachtwoord voor uw account. Uw andere toestellen worden afgemeld.',
  'cloud.passwordTooShort': 'Wachtwoord te kort: minstens 8 tekens.',
  'cloud.newPassword': 'Nieuw wachtwoord',
  'cloud.resetSave': 'Opslaan en aanmelden',
  'cloud.resetExpired':
    'Deze link is verlopen of werd al gebruikt. Vraag een nieuwe aan via “Wachtwoord vergeten?”.',
  'cloud.signedInAs': 'Aangemeld:',
  'cloud.logout': 'Afmelden',
  'cloud.deleteAccount': 'Account verwijderen…',
  'cloud.deleteTitle': 'Uw account verwijderen',
  'cloud.deleteIntro':
    'Hiermee worden het account {email} en de gegevens ervan definitief van de Tablo-server verwijderd:',
  'cloud.deleteWhat':
    'uw online projecten in uw persoonlijke werkruimte en in de werkruimtes waarvan u het enige lid bent, met hun foto’s, bestanden, geschiedenis, deellinks en sjablonen, en uw herstelkopieën;',
  'cloud.deleteShared':
    'in werkruimtes die u met anderen deelt, blijven de projecten bij het team; was u de enige eigenaar, dan wordt een ander lid eigenaar;',
  'cloud.deleteLocal':
    'projecten die op dit toestel bewaard zijn, blijven daar. Wilt u een online project houden, open het dan en bewaar het eerst als .tablo-bestand.',
  'cloud.deletePassword': 'Uw wachtwoord, ter bevestiging',
  'cloud.deleteConfirm': 'Mijn account definitief verwijderen',
  'cloud.deleteWrongPassword': 'Verkeerd wachtwoord.',
  'cloud.deleteOffline': 'Geen verbinding met de server: het account kan alleen online verwijderd worden.',
  'cloud.deleted': 'Uw account en de online gegevens ervan zijn verwijderd.',
  'cloud.workspace': 'Werkruimte',
  'cloud.newWorkspace': 'Nieuwe teamwerkruimte…',
  'cloud.create': 'Maken',
  'cloud.tab.projects': 'Projecten',
  'cloud.tab.members': 'Leden',
  'cloud.tab.templates': 'Teamsjablonen',
  'cloud.upload': 'Huidig project opslaan in “{name}”',
  'cloud.noProjects': 'Geen projecten in deze werkruimte.',
  'cloud.untitled': 'Zonder titel',
  'cloud.personal': 'Persoonlijke werkruimte: maak een teamwerkruimte om samen te werken.',
  'cloud.role.owner': 'eigenaar',
  'cloud.role.editor': 'bewerker',
  'cloud.role.viewer': 'lezer',
  'cloud.remove': 'Verwijderen',
  'cloud.addMember': 'Toevoegen',
  'cloud.rolesHint':
    'Bewerkers wijzigen projecten. Lezers kijken. Eigenaars beheren ook de leden. De persoon moet eerst een account maken.',
  'cloud.saveTemplate': 'Huidig project opslaan als teamsjabloon',
  'cloud.noTemplates': 'Geen teamsjablonen.',
  'cloud.brandTitle': 'Je schema’s,\nop elke werf.',
  'cloud.brand1': 'Online bewaard en gesynchroniseerd tussen computer, tablet en telefoon',
  'cloud.brand2': 'Samenwerken in een teamruimte',
  'cloud.brand3': 'Link om te bekijken voor de klant en het keuringsorganisme',
  'cloud.dataEurope': 'Gegevens gehost in Europa',
  'cloud.loginTitle': 'Aanmelden bij Tablo',
  'cloud.registerTitle': 'Een Tablo-account maken',
  'cloud.loginIntro': 'Welkom terug. Meld je aan om je online projecten terug te vinden.',
  'cloud.noAccount': 'Nog geen account?',
  'cloud.haveAccount': 'Al een account?',
  'cloud.showPassword': 'Wachtwoord tonen',
  'cloud.hidePassword': 'Wachtwoord verbergen',
  'cloud.workspaces': 'Werkruimtes',
  'cloud.searchProjects': 'Project zoeken',
  'cloud.uploadHere': 'Huidig project hier bewaren',
  'cloud.col.project': 'Project',
  'cloud.col.updated': 'Gewijzigd',
  'cloud.col.by': 'Door',
  'cloud.col.template': 'Sjabloon',
  'cloud.openNow': 'open',
  'cloud.noMatch': 'Geen project gevonden voor “{q}”.',
  'cloud.you': 'jij',
  'cloud.liveIn': 'Project online geopend in “{name}”',
  'cloud.liveOffline': 'Offline: wijzigingen worden verstuurd zodra er weer verbinding is',
  'cloud.menuProjects': 'Online projecten…',
  'cloud.menuMembers': 'Leden…',
  'cloud.menuTemplates': 'Teamsjablonen…',
  'cloud.accountMenu': 'Account',
  'cloud.emailPlaceholder': 'naam@bedrijf.be',
  'cloud.roleOf': 'Rol van',
  'cloud.memberEmail': 'E-mail van het nieuwe lid',
  'cloud.synced': '☁ Gesynchroniseerd',
  'cloud.syncing': '☁ Versturen… ({n})',
  'cloud.offline': '☁ Offline · {n} wijziging(en) wachten',
  'cloud.with': 'met {names}',
  'cloud.readOnly': 'alleen lezen',
  'cloud.rejected': 'Wijziging “{label}” ongedaan gemaakt: een andere gebruiker wijzigde deze elementen.',
  'cloud.error': 'Serverfout: {msg}',
  'cloud.uploaded': 'Project opgeslagen in “{name}”; wijzigingen worden gesynchroniseerd.',
  'cloud.teamLibrary': 'Teambibliotheek',
  'cloud.shareToTeam': 'Mijn symbolen en macro’s delen met het team',
  'cloud.sharedToTeam': 'Symbolen en macro’s gedeeld met “{name}”',
  'share.menu': 'Delen…',
  'share.title': 'Delen om te bekijken',
  'share.intro':
    'Met een link kunnen de klant, de architect of het keuringsorganisme de schema’s bekijken en downloaden, zonder account en zonder ze te kunnen wijzigen.',
  'share.live': 'Altijd de laatste versie',
  'share.atRevision': 'Vastgelegd op index {index}',
  'share.create': 'Link maken',
  'share.none': 'Geen links voor dit project.',
  'share.copy': 'Kopiëren',
  'share.handover': 'Overdrachtsdossier (PDF + QR-code)',
  'share.revoke': 'Uitschakelen',
  'viewer.status': 'Alleen lezen (gedeelde link)',
  'viewer.live': 'Gedeelde schema’s — alleen lezen',
  'viewer.revision': 'Schema’s op index {index} — alleen lezen',
  'viewer.download': 'PDF downloaden',
  'viewer.error': 'Ongeldige deellink: {msg}',

  'dxf.title': 'DXF-plan importeren',
  'dxf.info': 'DXF-bestand {version} · {w} × {h} m',
  'dxf.units': 'Tekeneenheid',
  'dxf.unitsStated': 'vermeld in het bestand',
  'dxf.unitsGuessed': 'geschat op basis van de grootte: te controleren',
  'dxf.scale': 'Schaal',
  'dxf.paperSize': 'Op het blad: {w} × {h} mm',
  'dxf.tooBig': 'Groter dan het tekengebied: kies een kleinere schaal of een A3-blad.',
  'dxf.layers': 'Lagen',
  'dxf.layerCount': '{n} element(en)',
  'dxf.hiddenLayer': 'verborgen in het bestand',
  'dxf.all': 'Alles',
  'dxf.none': 'Geen',
  'dxf.import': 'Importeren',
  'dxf.strays': '{n} los(se) element(en) ver van de tekening weggelaten.',
  'dxf.skipped': 'Niet overgenomen: {list}.',
  'dxf.dwg':
    'DWG-bestand (AutoCAD {version}): dit gesloten formaat kan niet rechtstreeks gelezen worden. Sla het op als DXF vanuit je CAD-programma (Opslaan als → DXF) of zet het om met ODA File Converter (gratis), en importeer dan de DXF.',
  'dxf.binary': 'Binaire DXF wordt niet ondersteund: sla het opnieuw op als ASCII-DXF (tekst).',
  'dxf.notDxf': 'Dit bestand is geen leesbare DXF.',
  'dxf.empty': 'Niets te importeren op de gekozen lagen.',
  'dxf.trueScale': 'Het plan staat op ware schaal: de schaal instellen is niet nodig.',

  'quote.menu': 'Offerte en materiaallijst (Excel)…',
  'quote.title': 'Offerte en materiaallijst',
  'quote.intro':
    'Materiaal uit de eendraadschema’s, kabels volgens de lengte van de kringen en een lijn voor arbeid. De prijzen komen uit je prijslijst (groothandel); alles blijft aanpasbaar in het bestand.',
  'quote.priceList': 'Prijslijst',
  'quote.importPrices': 'Prijslijst importeren (CSV, Excel)…',
  'quote.priceListInfo': '{name} · {n} prijzen',
  'quote.noPriceList': 'Geen prijslijst: de prijzen worden in het bestand ingevuld.',
  'quote.removePrices': 'Verwijderen',
  'quote.noPrices': 'Geen prijzen gevonden: de lijst moet een kolom referentie en een kolom prijs hebben.',
  'quote.vat': 'Btw',
  'quote.vat6': '6 % (renovatie van een woning ouder dan 10 jaar)',
  'quote.vat21': '21 %',
  'quote.waste': 'Snijverlies kabel (%)',
  'quote.discount': 'Korting op materiaal (%)',
  'quote.missingLengths': 'Kabel niet meegeteld voor {n} kring(en) zonder lengte: {list}.',
  'quote.lines': '{n} lijn(en), waarvan {priced} met een prijs',
  'quote.totals': 'Totaal excl. btw {sub} · btw {vat} · incl. btw {total}',
  'quote.xlsx': 'Excel (.xlsx)',
  'quote.csv': 'CSV',
  'quote.fromSurvey':
    'De opname heeft nog geen schema’s opgeleverd: maak ze, zodat de offerte het materiaal ervan bevat.',
  'quote.file': 'offerte',
  'quote.colRef': 'Ref.',
  'quote.colDesc': 'Omschrijving',
  'quote.colQty': 'Aantal',
  'quote.colPrice': 'EP excl. btw',
  'quote.colSource': 'Bron',
  'quote.sourceStale': '{name}: de laatste update is mislukt; de prijzen van {date} worden gebruikt.',
  'quote.serverPricesDown':
    'De prijzen van de groothandels konden niet geladen worden (geen verbinding): offerte berekend met de andere bronnen.',
  'quote.sourceReference': 'Tablo-referentieprijzen (België)',
  'quote.sourceUpdated': 'bijgewerkt op {date}',
  'quote.sourceOrder': 'Volgorde als een prijs ontbreekt: {list}',
  'quote.preferredSource': 'Voorkeursbron voor prijzen',
  'quote.downloadPrices': 'Prijzen downloaden (Excel)…',
  'quote.priceListFile': 'Tablo-prijzen.xlsx',
  'quote.useLibrary':
    'Ontbrekende prijzen aanvullen met de Belgische referentieprijzen (gecontroleerd op {date})',
  'quote.libraryPrice':
    'Tablo-referentieprijzen zijn publieke prijzen excl. btw: die van uw groothandel liggen lager.',
  'quote.labourHours': 'Arbeid (uren)',

  'survey.mode': 'Opname ter plaatse',
  'survey.menu': 'Opname ter plaatse (gsm, tablet)',
  'survey.homeTitle': 'Opnames',
  'survey.intro':
    'Neem de installatie ruimte per ruimte op en fotografeer het bord: Tablo maakt er het eendraadschema en de situatieschema’s van. Werkt zonder netwerk.',
  'survey.new': 'Nieuwe opname',
  'survey.newTitle': 'Opname van {date}',
  'survey.current': 'Open project',
  'survey.continue': 'Verdergaan',
  'survey.onDevice': 'Op dit toestel',
  'survey.cloudTeam': 'Cloud — {team}',
  'survey.noProjects': 'Geen projecten op dit toestel.',
  'survey.offlineCopy': 'offline kopie',
  'survey.cloudBadge': 'Cloud',
  'survey.remove': 'Van dit toestel verwijderen',
  'survey.removeConfirm': '“{name}” van dit toestel verwijderen? Een cloudproject blijft op de server.',
  'survey.signIn': 'Aanmelden bij de cloud',
  'survey.install': 'App installeren',
  'survey.installIos': 'Installeren: knop Delen, dan “Zet op beginscherm”.',
  'survey.storageWarn': 'Installeer de app: anders kan de browser de opnames op dit toestel wissen.',
  'survey.openEditor': 'Openen in de editor',
  'survey.back': 'Terug',
  'survey.tabRooms': 'Ruimtes',
  'survey.tabBoard': 'Bord',
  'survey.tabChecks': 'Controle',
  'survey.tabInfo': 'Project',
  'survey.addRoom': 'Ruimte toevoegen',
  'survey.roomName': 'Naam van de ruimte',
  'survey.roomType': 'Soort ruimte',
  'survey.level': 'Verdieping',
  'survey.noRooms': 'Begin met de ruimte waar je bent.',
  'survey.points': 'Punten',
  'survey.morePoints': 'Andere punten…',
  'survey.noPoints': 'Geen punten in deze ruimte: voeg ze hieronder toe.',
  'survey.circuit': 'Kring',
  'survey.noCircuit': 'Geen kring',
  'survey.chooseCircuit': 'Kring van deze punten',
  'survey.newCircuit': 'Nieuwe kring',
  'survey.notes': 'Notities',
  'survey.photos': 'Foto’s',
  'survey.takePhoto': 'Foto',
  'survey.fromGallery': 'Galerij',
  'survey.removePhoto': 'Foto verwijderen',
  'survey.deleteRoom': 'Ruimte verwijderen',
  'survey.deleteRoomConfirm': '“{name}” en de punten ervan verwijderen?',
  'survey.pointCount': '{n} punt(en)',
  'survey.unassigned': '{n} zonder kring',
  'survey.addBoard': 'Bord toevoegen',
  'survey.mainBoard': 'Hoofdbord',
  'survey.boardName': 'Naam van het bord',
  'survey.boardPhotos': 'Foto’s van het bord',
  'survey.readBoard': 'Bord lezen (AI)',
  'survey.reading': 'Foto lezen…',
  'survey.readResult': '{circuits} kring(en) en {rcds} differentieel(en) herkend.',
  'survey.readApply': 'Bord invullen',
  'survey.readKeeps': 'Kringen die al opgenomen zijn, worden in volgorde bijgewerkt en houden hun punten.',
  'survey.readError': 'Lezen mislukt: {msg}',
  'survey.mainSwitch': 'Hoofdschakelaar',
  'survey.mainRcd': 'Hoofddifferentieel',
  'survey.none': 'Geen',
  'survey.rcds': 'Differentiëlen',
  'survey.addRcd': 'Differentieel toevoegen',
  'survey.rcdLabel': 'Differentieel {ma} mA',
  'survey.mainBus': 'Rechtstreeks onder de hoofdschakelaar',
  'survey.circuits': 'Kringen',
  'survey.addCircuit': 'Kring toevoegen',
  'survey.circuitLabel': 'Omschrijving',
  'survey.circuitLabelPh': 'bv. Stopcontacten keuken',
  'survey.device': 'Beveiliging',
  'survey.devMCB': 'Automaat',
  'survey.devRCBO': 'Diff.-automaat',
  'survey.devFuse': 'Smeltveiligheid',
  'survey.rating': 'Nominale stroom (A)',
  'survey.curve': 'Curve',
  'survey.poles': 'Polen',
  'survey.section': 'Doorsnede (mm²)',
  'survey.cableType': 'Kabel',
  'survey.cores': 'Aders',
  'survey.length': 'Lengte (m)',
  'survey.rcdOf': 'Differentieel',
  'survey.legacy': 'Oud gedeelte (vóór 1 oktober 1981)',
  'survey.circuitPoints': 'Gevoede punten',
  'survey.deleteCircuit': 'Kring verwijderen',
  'survey.deleteCircuitConfirm': 'Kring {code} verwijderen? De punten blijven, zonder kring.',
  'survey.deleteBoard': 'Bord verwijderen',
  'survey.deleteBoardConfirm': '“{name}” en de kringen ervan verwijderen?',
  'survey.deleteRcd': 'Differentieel verwijderen',
  'survey.earth': 'Aardverbinding',
  'survey.earthOhm': 'Gemeten spreidingsweerstand (Ω)',
  'survey.sensitivity': 'Gevoeligheid (mA)',
  'survey.rcdType': 'Type',
  'survey.checksOk': 'Niets te melden.',
  'survey.fromDiagram': '{std}-regel, op de schema’s die gemaakt zullen worden',
  'survey.generate': 'Schema’s maken',
  'survey.regenerate': 'Schema’s bijwerken',
  'survey.generated': '{n} blad(en) gemaakt vanuit de opname.',
  'survey.stale': 'De opname is gewijzigd sinds de schema’s gemaakt werden.',
  'survey.upToDate': 'De schema’s volgen de opname.',
  'survey.notGenerated': 'Nog geen schema’s: maak ze wanneer de opname volledig is (of nu al, om te kijken).',
  'survey.regenerateWarn':
    'De bladen die vanuit de opname gemaakt zijn, worden vervangen: wat er met de hand op gewijzigd werd, gaat verloren.',
  'survey.statusSaved': 'Opgeslagen',
  'survey.statusSynced': 'Gesynchroniseerd',
  'survey.statusPending': 'Te versturen: {n}',
  'survey.statusOffline': 'Offline',
  'survey.statusFiles': 'Foto’s te versturen: {n}',
  'survey.statusFile': 'Bestand {name}',
  'survey.keepOnDevice': 'Op dit toestel bewaren',
  'survey.kept': 'Project bewaard op dit toestel.',
  'survey.pdf': 'PDF van de schema’s',
  'survey.quantity': 'Aantal',
  'survey.deletePoint': 'Verwijderen',
  'survey.upload': 'In de cloud zetten ({team})',
  'survey.cloudSection': 'Cloud',
  'survey.inCloud': 'In de cloud — {team}',
  'survey.done': 'OK',

  // ---------- after the drawing: measurements, inspection reports, invoices ----------
  'menu.inspection': 'Keuring',
  'panel.photos': 'Foto’s',
  'quote.invoice': 'Factureren…',
  'survey.measure': 'Metingen invoeren',
  'survey.pointPhotos': 'Foto’s van het punt ({n})',
  'measure.menu': 'Metingen en testen…',
  'measure.earth': 'Aardverbinding',
  'measure.earthOhm': 'Spreidingsweerstand (Ω)',
  'measure.title': 'Metingen en testen',
  'measure.intro':
    'Eén kaart per kring en per differentieel van het schema. Elke waarde wordt met haar grens vergeleken zodra ze ingevoerd is; de regels R-TEST melden wat faalt of ontbreekt.',
  'measure.date': 'Datum van de metingen',
  'measure.circuits': 'Kringen',
  'measure.rcds': 'Differentiëlen',
  'measure.insulation': 'Isolatie (MΩ)',
  'measure.testVoltage': 'Meetspanning',
  'measure.continuity': 'Continuïteit PE (Ω)',
  'measure.loop': 'Foutlus Zs (Ω)',
  'measure.noLimit': 'zekering: geen grens',
  'measure.tripMs': 'Uitschakeltijd (ms)',
  'measure.tripMa': 'Uitschakelstroom (mA)',
  'measure.testButton': 'Testknop',
  'measure.trips': 'Schakelt uit',
  'measure.noTrip': 'Schakelt niet uit',
  'measure.nothing': 'Geen kring of differentieel op het schema: teken of genereer eerst het eendraadschema.',
  'measure.progress': '{m}/{c} kringen gemeten · {r}/{n} differentiëlen getest · {f} niet conform',
  'measure.csv': 'Exporteren (CSV)',
  'measure.file': 'metingen',
  'measure.tx': 'Meting {ref}',
  'inspection.menu': 'Proces-verbalen en opmerkingen…',
  'inspection.title': 'Proces-verbalen van keuring',
  'inspection.read': 'Proces-verbaal lezen (foto of pdf)…',
  'inspection.reading': 'Proces-verbaal wordt gelezen…',
  'inspection.readFailed': 'Lezen mislukt: {error}',
  'inspection.manual': 'Handmatig invoeren',
  'inspection.none':
    'Nog geen proces-verbaal voor dit project. Lees dat van het erkend organisme, of voer de opmerkingen handmatig in.',
  'inspection.pick': 'Proces-verbaal',
  'inspection.body': 'Erkend organisme',
  'inspection.reference': 'Nr. van het proces-verbaal',
  'inspection.date': 'Datum van het bezoek',
  'inspection.inspector': 'Keurder',
  'inspection.result': 'Resultaat',
  'inspection.conform': 'Gelijkvormig',
  'inspection.notConform': 'Niet gelijkvormig',
  'inspection.deadline': 'Termijn voor de werken',
  'inspection.pages': 'Pagina’s van het proces-verbaal',
  'inspection.remarksFound': '{n} opmerking(en) gelezen — vink uit wat u niet wilt overnemen',
  'inspection.addToProject': 'Aan het project toevoegen ({n})',
  'inspection.txAdd': 'Proces-verbaal van keuring',
  'inspection.remarks': 'Opmerkingen — {open} op te lossen van {n}',
  'inspection.remarksTitle': 'Opmerkingen van de keuring',
  'inspection.openCount': '{n} op te lossen van {total}',
  'inspection.pack': 'Dossier voor herkeuring (pdf)',
  'inspection.packFile': 'dossier herkeuring',
  'inspection.delete': 'Proces-verbaal verwijderen',
  'inspection.deleteConfirm':
    'Het proces-verbaal en zijn {n} opmerking(en) worden uit het project verwijderd.',
  'remark.one': 'opmerking',
  'remark.add': 'Opmerking toevoegen',
  'remark.open': 'Op te lossen',
  'remark.fixed': 'Opgelost',
  'remark.text': 'Opmerking',
  'remark.article': 'Artikel',
  'remark.ref': 'Kring of punt',
  'remark.fixNote': 'Wat er gedaan is',
  'remark.fixNoteHint': 'Bv. differentieel 30 mA type A toegevoegd op kring B',
  'remark.photos': 'Foto’s van de herstelling',
  'remark.delete': 'Opmerking verwijderen',
  'invoice.menu': 'Facturen…',
  'invoice.title': 'Facturen',
  'invoice.intro':
    'Uitgereikte facturen blijven in het project en worden niet meer gewijzigd. Elke factuur kan als pdf (met een QR-code om te betalen) worden gedownload en, tussen ondernemingen, als Peppol-bestand (UBL) voor de e-facturatie die in België sinds 2026 verplicht is.',
  'invoice.none': 'Nog geen factuur voor dit project.',
  'invoice.new': 'Nieuwe factuur',
  'invoice.creditNote': 'Creditnota…',
  'invoice.newCreditNote': 'Nieuwe creditnota',
  'invoice.kindCredit': 'creditnota',
  'invoice.creditFor':
    'Creditnota voor factuur {n} van {date}: verwijder de lijnen of verlaag de aantallen die niet gecrediteerd worden.',
  'invoice.creditHint': 'Deze factuur corrigeren of annuleren (geheel of gedeeltelijk)',
  'invoice.kindDeposit': 'voorschot',
  'invoice.depositPct': 'Voorschot (%)',
  'invoice.makeDeposit': 'Voorschotfactuur maken',
  'invoice.depositHint':
    'Vervangt de lijnen door één lijn: dit deel van hun totaal, aan hun btw-tarief. De eindfactuur trekt het af.',
  'invoice.depositLine': 'Voorschot {pct} % — {ref}',
  'invoice.depositDeducted': 'Gefactureerd voorschot (factuur {n} van {date})',
  'invoice.deductDeposits': 'Voorschotfacturen aftrekken ({n})',
  'invoice.isDeposit': 'Voorschotfactuur: de eindfactuur trekt ze af.',
  'invoice.vatPart': 'Btw {pct} % op {base}: {vat}',
  'invoice.seriesTeam':
    'Nummer gegeven door de werkruimte “{team}” bij uitgifte: één reeks voor het hele bedrijf.',
  'invoice.seriesOffline': 'Het nummer komt van de werkruimte “{team}”: maak verbinding om uit te geven.',
  'invoice.seriesStart':
    'Nieuwe factuurreeks {prefix} voor “{team}”. Zet u een bestaande nummering voort? Volgend nummer:',
  'invoice.seriesStartLabel': 'Volgend nummer van de reeks',
  'invoice.seriesSet': 'Instellen',
  'invoice.emailAction': 'E-mail…',
  'invoice.emailTitle': '{n} naar de klant mailen',
  'invoice.emailTo': 'Aan',
  'invoice.emailMessage': 'Uw bericht (optioneel)',
  'invoice.emailXml': 'Peppol-bestand bijvoegen (zakelijke klant)',
  'invoice.emailReply':
    'Verstuurd vanaf het adres van Tablo op uw naam; antwoorden van de klant komen bij {email} terecht.',
  'invoice.emailAccount': 'het adres van uw account',
  'invoice.emailSend': 'Versturen',
  'invoice.emailSent': '{n} verstuurd naar {to}.',
  'invoice.emailFailed': 'Niet verstuurd: {msg}',
  'billit.pitch': 'Verstuur uw facturen via Peppol en volg hun betaling op met Billit:',
  'billit.connect': 'Verbinden met Billit',
  'billit.waiting': 'Wachten op Billit…',
  'billit.connectedTo': 'Verbonden met Billit: geef uw facturen door vanuit de lijst.',
  'billit.disconnect': 'Ontkoppelen',
  'billit.title': '{n} doorgeven aan Billit',
  'billit.explain':
    'De factuur komt ongewijzigd in uw Billit-account (zelfde nummer, lijnen en pdf). Billit verstuurt ze, volgt de betaling op en geeft ze door aan uw boekhouder.',
  'billit.peppolNow': 'Nu via Peppol versturen',
  'billit.noPeppol':
    'Niet via Peppol (particuliere klant, of Peppol-id’s ontbreken): Billit bewaart ze zodat u ze kunt versturen',
  'billit.send': 'Naar Billit sturen',
  'billit.sent': '{n} staat in Billit.',
  'billit.sentPeppol': '{n} staat in Billit en vertrekt via Peppol.',
  'billit.failed': 'Billit: {msg}',
  'billit.tx': 'Factuur {n} doorgeven aan Billit',
  'billit.statusHint': 'De status bij Billit lezen',
  'billit.paid': 'betaald',
  'billit.status.ToSend': 'te versturen',
  'billit.status.ToPay': 'verstuurd, te betalen',
  'billit.status.Paid': 'betaald',
  'billit.connectedNotice': 'Verbonden met Billit.',
  'billit.failedNotice': 'Billit werd niet verbonden: probeer opnieuw vanuit de facturen.',
  'invoice.problem.negativeTotal': 'Het totaal is negatief: maak liever een creditnota.',
  'invoice.problem.negativePrice':
    'Een eenheidsprijs is negatief: trek een bedrag af met een negatief aantal.',
  'invoice.issue': 'Factuur uitreiken',
  'invoice.txIssue': 'Factuur {n}',
  'invoice.duplicate': 'Nummer {n} wordt al gebruikt in dit project.',
  'invoice.file': 'Factuur',
  'invoice.seller': 'Uw gegevens',
  'invoice.sellerKept': '(bewaard op dit toestel)',
  'invoice.buyer': 'Klant',
  'invoice.buyerHint':
    'Particulier: laat het btw-nummer leeg, de factuur gaat als pdf. Onderneming: het ondernemingsnummer is haar Peppol-id (0208).',
  'invoice.details': 'Factuur',
  'invoice.name': 'Naam of benaming',
  'invoice.vatNumber': 'Btw-nummer',
  'invoice.street': 'Straat en nummer',
  'invoice.postcode': 'Postcode',
  'invoice.city': 'Gemeente',
  'invoice.country': 'Land',
  'invoice.email': 'E-mail',
  'invoice.peppolId': 'Peppol-id (optioneel)',
  'invoice.number': 'Nr.',
  'invoice.vat': 'Btw',
  'invoice.reverseCharge': 'Verlegging van heffing (medecontractant)',
  'invoice.issueDate': 'Datum',
  'invoice.dueDate': 'Vervaldag',
  'invoice.orderRef': 'Referentie van de klant (bestelbon, offerte)',
  'invoice.paymentRef': 'Gestructureerde mededeling',
  'invoice.note': 'Vermelding',
  'invoice.lines': 'Lijnen',
  'invoice.addQuoteLines': 'Offertelijnen overnemen',
  'invoice.addLine': 'Lijn',
  'invoice.unit': 'Eenheid',
  'invoice.unit.C62': 'stuk',
  'invoice.unit.MTR': 'meter',
  'invoice.unit.HUR': 'uur',
  'invoice.amount': 'Bedrag',
  'invoice.total': 'Totaal incl. btw',
  'invoice.peppolReady':
    '✓ Klaar voor Peppol: het UBL-bestand kan via uw Peppol-toegangspunt of het Hermes-portaal worden verstuurd.',
  'invoice.pdfOnly':
    'Factuur als pdf (klant zonder Belgisch ondernemingsnummer: geen verzending via Peppol).',
  'invoice.peppolHint': 'Peppol BIS Billing 3.0-bestand (UBL) om via uw toegangspunt te versturen',
  'invoice.peppolMissing': 'De klant heeft geen Peppol-id (Belgisch ondernemingsnummer)',
  'invoice.problem.sellerName': 'Uw naam of benaming ontbreekt.',
  'invoice.problem.sellerVat': 'Uw btw-nummer ontbreekt of is niet geldig.',
  'invoice.problem.sellerAddress': 'Uw adres is onvolledig.',
  'invoice.problem.sellerIban': 'De IBAN is niet geldig.',
  'invoice.problem.buyerName': 'De naam van de klant ontbreekt.',
  'invoice.problem.buyerAddress': 'Het adres van de klant ontbreekt.',
  'invoice.problem.buyerVatReverse': 'Verlegging van heffing vereist het btw-nummer van de klant.',
  'invoice.problem.buyerVat': 'Het btw-nummer van de klant is niet geldig.',
  'invoice.problem.noLines': 'De factuur heeft geen lijnen.',
  'invoice.problem.lineDescription': 'Een lijn heeft geen omschrijving.',
  'invoice.problem.number': 'Het factuurnummer ontbreekt.',
  'invoice.problem.dates': 'De datums zijn niet geldig (vervaldag vóór de factuurdatum?).',
  'invoice.problem.paymentRef': 'De gestructureerde mededeling is niet geldig.',

  // Rondleiding (Tour.tsx, tourSteps.ts)
  'tour.menu': 'Rondleiding',
  'tour.welcome': 'Volg de rondleiding',
  'tour.begin': 'Beginnen',
  'tour.next': 'Volgende',
  'tour.back': 'Terug',
  'tour.replay': 'Opnieuw tonen',
  'tour.skipDemo': 'Demo overslaan',
  'tour.restart': 'Opnieuw beginnen',
  'tour.finish': 'Afronden',
  'tour.close': 'Rondleiding sluiten',
  'tour.playPart': 'Dit deel opnieuw tonen',
  'tour.prevPart': 'Vorig deel',
  'tour.nextPart': 'Volgend deel',
  'tour.cap.aiBoard': 'Een bord lezen van een foto',
  'tour.cap.ib': 'Bedrijfsstroom van de verlichtingskring',
  'tour.sample.circuit': 'Keuken',
  'side.label': 'Zijbalk',
  'side.fold': 'Zijbalk inklappen',
  'side.unfold': 'Zijbalk uitklappen',
  'palette.fold': 'Symbolenpaneel inklappen',
  'palette.unfold': 'Symbolenpaneel uitklappen',
  'side.assistant': 'AI-assistent',
  'side.selectHint':
    'Klik op een symbool of een draad op het blad om hier de eigenschappen te zien en te wijzigen.',
  'side.assistHint':
    'Vraag een wijziging in uw eigen woorden, bijvoorbeeld “voeg een laadpaal op 32 A toe en maak dan het meerdraadsschema”. U ziet wat er verandert vóór u het toepast.',
  'side.assistSignedOut':
    'De assistent past het schema aan op basis van een vraag in uw eigen woorden. Meld u aan om hem te gebruiken.',
  'tour.area.assistant': 'De AI-assistent',
  'tour.area.rail': 'Eén pictogram per sectie',
  'tour.cap.assistant': 'Geopend met AI → Assistent',
  'tour.cap.assistSend': 'Verzenden en dan het voorstel nakijken',
  'tour.cap.assistSignIn': 'De assistent vraagt een account',
  'tour.sample.assist': 'Voeg een laadpaal op 32 A toe en maak dan het meerdraadsschema',
  'tour.tipLabel': 'Goede praktijk',
  'tour.chapter': 'Deel {n}/{total}',
  'tour.ch1': 'Een project starten',
  'tour.ch2': 'Met de hand tekenen',
  'tour.ch3': 'De kringentabel',
  'tour.ch4': 'De controle',
  'tour.ch5': 'De andere schema’s',
  'tour.ch6': 'Opslaan en overdragen',
  'tour.sample.title': 'Woning Peeters',
  'tour.sample.owner': 'Jan Peeters',
  'tour.sample.address': 'Stationsstraat 12, 3000 Leuven',
  'tour.area.toolbar': 'Menu’s en gereedschap',
  'tour.area.palette': 'Symbolen',
  'tour.area.canvas': 'Het blad',
  'tour.area.inspector': 'Zijbalk: selectie, AI-assistent, blad',
  'tour.area.sheets': 'Bladen',
  'tour.area.status': 'Status en teller van de controle',
  'tour.cap.template': 'Woning, appartement of werkplaats, kant-en-klaar',
  'tour.cap.survey': 'Uit uw opname ter plaatse',
  'tour.cap.example': 'Een volledige woning om te verkennen',
  'tour.cap.standard': 'De regels die de controle toepast',
  'tour.cap.locale': 'De taal van de schema’s',
  'tour.cap.icc': 'Nodig om het onderbrekingsvermogen te controleren',
  'tour.cap.titleBlock': 'Het titelvak vult zichzelf in',
  'tour.cap.search': 'Een symbool op naam zoeken',
  'tour.cap.tag': 'Automatisch W1 genoemd',
  'tour.cap.label': 'Het label volgt de eigenschappen',
  'tour.cap.drop': 'Spanningsval tegenover de grens',
  'tour.cap.codes': 'Kringcodes op volgorde',
  'tour.cap.statusChecks': 'Altijd zichtbaar',
  'tour.cap.banner': 'Gemaakt uit het eendraadschema',
  'tour.cap.sheetKinds': 'Soorten bladen',
  'tour.cap.revisions': 'Revisie A, B… vastleggen',
  'tour.cap.exportFull': 'De volledige PDF voor de overdracht',
  'tour.cap.quote': 'Offerte en materiaallijst',
  'tour.cap.cloud': 'Synchronisatie, team en delen',

  'tour.intro.title': 'Teken uw eerste installatie',
  'tour.intro.body':
    'In een tiental minuten start u een project en tekent u een klein eendraadschema; daarna doet Tablo de rest: kringen, berekeningen, de AREI-controle en het meerdraadsschema.\nElke stap **licht een deel van het scherm uit** en toont het, deel per deel: de pijlen **‹ ›** — of de toetsen **← →** — spelen het vorige of volgende deel. Op het einde van een stap staan alle paragrafen onder elkaar, met de goede praktijk; klik op een paragraaf om ze opnieuw te zien. Probeer het zelf tussen de delen door en klik dan op **Volgende**.\nDe rondleiding werkt in een nieuw, leeg project: hou het of gooi het weg op het einde.',
  'tour.intro.tip':
    'Tablo is gemaakt voor een computer met muis en toetsenbord. Op een gsm opent het de **opname ter plaatse**: noteer ruimtes en punten op de werf en werk de schema’s hier af.',

  'tour.workspace.title': 'Uw werkruimte',
  'tour.workspace.p1':
    '**Bovenbalk**: de menu’s (Bestand, Schema, Keuring, AI), ongedaan maken, het gereedschap en de panelen Kringen en AREI-controle.',
  'tour.workspace.p2': '**Symbolen** links: de symbolen voor de soort blad waarop u werkt.',
  'tour.workspace.p3':
    '**Het blad** in het midden, een echt tekenkader met titelvak. Scrollwiel of knijpen om te zoomen, middelste knop of Spatie + slepen om te verschuiven, **F** om alles te tonen.',
  'tour.workspace.p4':
    'De **zijbalk** rechts bestaat uit secties die open- en dichtklappen: **Selectie** (de eigenschappen van wat geselecteerd is), de **AI-assistent** en **Blad** (naam, soort, formaat, projectgegevens).',
  'tour.workspace.p5':
    'De knop bovenaan klapt de zijbalk in tot een rij pictogrammen, voor meer plaats op het blad. Klik op een pictogram om zijn sectie te tonen; klik opnieuw, of op het blad, om ze weg te leggen.',
  'tour.workspace.p6': '**Bladen** onderaan, dan de **statusbalk** met de teller van de controle.',
  'tour.workspace.tip':
    'Leer vroeg het gereedschap met één toets — **V** selecteren, **W** draad, **T** tekst, **R** draaien, **F** alles tonen. Linkerhand op het toetsenbord, rechterhand op de muis: zo tekent u het snelst.',

  'tour.start.title': 'Drie manieren om te beginnen',
  'tour.start.p1':
    '**Bestand → Nieuw vanuit een sjabloon**: een woning, een appartement of een driefasige werkplaats, bord al getekend.',
  'tour.start.p2': '**Bestand → Opname ter plaatse**: wat u op de gsm noteerde, wordt de schema’s.',
  'tour.start.p3': '**Bestand → Voorbeeld**: een volledige woning om rond te kijken.',
  'tour.start.p4': '**AI → Foto van het bord importeren** leest een bestaand bord toestel per toestel.',
  'tour.start.tip':
    'Begin een echte opdracht niet op een leeg blad: vertrek van een sjabloon of van uw opname en pas aan. Het bord staat dan vanaf de eerste minuut volgens het AREI.',

  'tour.info.title': 'Eerst de projectgegevens',
  'tour.info.p1':
    '**Bestand → Projectgegevens** vult het titelvak van elk blad: project, eigenaar, adres van de installatie, auteur.',
  'tour.info.p2': 'De **norm** bepaalt welke regels de controle toepast: AREI, NF C 15-100 of NEN 1010.',
  'tour.info.p3':
    'De **taal van de schema’s** kan verschillen van die van de interface: een Franstalige interface voor een Vlaamse klant, bijvoorbeeld.',
  'tour.info.p4': 'Het aardingsstelsel, de spanning en de **verwachte kortsluitstroom** aan de oorsprong.',
  'tour.info.p5': '**OK**: het titelvak van elk blad vult zichzelf in.',
  'tour.info.tip':
    'Vul ze in vóór u tekent. Geef de kortsluitstroom aan de oorsprong op, anders kan het onderbrekingsvermogen niet gecontroleerd worden, en teken in de taal van de klant: dat lezen de klant en de keurder.',

  'tour.palette.title': 'Het symbolenpalet',
  'tour.palette.p1':
    'De symbolen staan per familie (verdeling, beveiliging, stopcontacten, verlichting…) en gefilterd volgens de soort blad. Typ in het zoekvak om er een op naam te vinden.',
  'tour.palette.p2':
    'Klik op een symbool en dan op het blad — hier de **voeding**. Ze krijgt meteen haar referentie, W1.',
  'tour.palette.tip':
    'Het palet toont alleen wat op deze soort blad hoort. Vink **Alle symbolen** enkel aan als u er echt een van elders nodig hebt, en maak wat ontbreekt met **Nieuw symbool…** onderaan in plaats van het met losse lijnen te tekenen.',

  'tour.place.title': 'De toestellen plaatsen',
  'tour.place.p1':
    'Plaats het bord van boven naar onder: een **differentieelschakelaar** (Q1), een **automaat** (Q2) en een **stopcontact** (X1). Alles valt op een raster van 2,5 mm.',
  'tour.place.p2':
    '**R** draait en **M** spiegelt het symbool dat u plaatst of de selectie; **Esc** legt het gereedschap neer.',
  'tour.place.tip':
    'Teken het bord zoals het gelezen wordt: voeding, hoofdschakelaar of differentieel, automaten en dan de punten. Hou de referenties die Tablo geeft: het meerdraadsschema, de kringenlijst en de bordetiketten steunen erop.',

  'tour.wire.title': 'Verbinden met draden',
  'tour.wire.p1':
    'Druk op **W**, klik op een **aansluitpunt** — het uiteinde van een symbool licht op als u erop staat — en dan op het volgende.',
  'tour.wire.p2':
    'De hoeken worden voor u getekend: verbind de differentieel met de automaat, dan de automaat met het stopcontact.',
  'tour.wire.p3':
    'Klik op het lege raster om zelf een hoek toe te voegen; begin of eindig op een draad voor een T-aftakking. **Backspace** verwijdert de laatste hoek, **Esc** annuleert.',
  'tour.wire.tip':
    'Laat een draad altijd eindigen op een aansluitpunt of op een andere draad, nooit er net naast. De verbindingen — niet de tekening — lezen de kringen, de berekeningen en de controle: een draad die alleen verbonden lijkt, is het niet.',

  'tour.inspector.title': 'De eigenschappen instellen',
  'tour.inspector.p1':
    'Klik met het selectiegereedschap (**V**) op een toestel: de sectie **Selectie** van de zijbalk toont de referentie en alles wat het draagt — kaliber, curve, gevoeligheid, kabel, catalogusreferentie, foto’s.',
  'tour.inspector.p2':
    'Hier wordt de automaat **20 A, curve C**, en het label op het blad volgt. Dubbelklik op een toestel om naar het eerste veld te springen.',
  'tour.inspector.tip':
    'Vul echte waarden in, geen voorlopige. Kalibers, curves, gevoeligheden en kabels voeden de berekeningen en de controle: niets op een Tablo-schema is versiering.',

  'tour.edit.title': 'Verplaatsen, kopiëren, ongedaan maken',
  'tour.edit.p1': 'Sleep een toestel om het te verplaatsen: de draden volgen.',
  'tour.edit.p2': '**{mod}Z** maakt ongedaan — het stopcontact gaat terug. **{mod}⇧Z** herstelt.',
  'tour.edit.p3':
    'Sleep over een lege zone om te selecteren (van links naar rechts: wat er helemaal in ligt; van rechts naar links: alles wat geraakt wordt). **Shift**-klik voegt toe, **{mod}D** dupliceert, de pijltjes verschuiven 2,5 mm (10 met Shift), **Delete** wist.',
  'tour.edit.tip':
    'Ongedaan maken onthoudt de laatste 500 stappen: probeer gerust. Sla toch op vóór grote ingrepen zoals bladen maken of hernummeren: uw vangnet is het bestand, niet de geschiedenis.',

  'tour.circuits.title': 'De kringentabel',
  'tour.circuits.p1':
    '**Kringen** opent de tabel van het bord: één rij per automaat, met omschrijving, kaliber, curve, differentieel, kabel, doorsnede, lengte, plaatsingswijze en punten.',
  'tour.circuits.p2':
    'Wijzig een cel en het schema volgt — hier een omschrijving. De tabel en het blad zijn twee zichten op hetzelfde bord.',
  'tour.circuits.tip':
    'Zo bouwt u een eendraadschema het snelst en het veiligst. Teken de kop van het bord met de hand als u wil, en voeg daarna de kringen hier toe en pas ze hier aan, in plaats van ze één voor één te tekenen.',

  'tour.addCircuit.title': 'Een kring in één klik',
  'tour.addCircuit.p1':
    'Kies de differentieelschakelaar waaronder de kring komt en de soort — stopcontacten, verlichting of één toestel — en dan **Kring toevoegen**.',
  'tour.addCircuit.p2':
    'Tablo tekent de automaat, de punten en de rail, maakt plaats en vult de gebruikelijke waarden in: 16 A / 1,5 mm² voor verlichting, 20 A / 2,5 mm² voor stopcontacten.',
  'tour.addCircuit.tip':
    'Zet elke kring meteen onder de juiste differentieel: badkamer, wasmachine, vaatwasser en droogkast onder de 30 mA. Beperk kringen met stopcontacten tot 8 punten — de tabel telt ze voor u.',

  'tour.calc.title': 'Berekeningen terwijl u typt',
  'tour.calc.p1':
    '**Ib** is de bedrijfsstroom; **Iz** wat de kabel aankan volgens isolatie en plaatsingswijze.',
  'tour.calc.p2':
    '**ΔU** is de spanningsval (meestal 3 % voor verlichting, 5 % voor de rest). Met 28 m in 1,5 mm² voldoet de verlichtingskring niet: de waarde wordt rood.',
  'tour.calc.p3':
    '**L max** is de grootste lengte die nog tegen kortsluiting beveiligd is. Elke rode waarde voldoet niet.',
  'tour.calc.tip':
    'Vul de echte kabellengtes in, gemeten op het plan of ter plaatse: spanningsval en maximale lengte zijn maar zo goed als die cijfers. Het zijn ontwerphulpen — de metingen bij de keuring beslissen.',

  'tour.renumber.title': 'De kringen nummeren',
  'tour.renumber.p1':
    '**Kringen nummeren** zet de codes weer op volgorde — A1, A2, A3… — nadat kringen toegevoegd, verplaatst of verwijderd zijn.',
  'tour.renumber.p2':
    'Elk punt draagt de code van zijn kring, dezelfde op het eendraadschema, het situatieschema en de bordetiketten.',
  'tour.renumber.tip':
    'Hernummer zodra het bord vastligt en vóór u plannen, etiketten en het overdrachtsdossier maakt — niet erna. Codes die wijzigen terwijl de documenten op de werf liggen, zorgen voor fouten.',

  'tour.assistant.title': 'Vraag het de AI-assistent',
  'tour.assistant.p1':
    '**AI → Assistent** opent de sectie **AI-assistent** van de zijbalk. Ze blijft daar terwijl u tekent en onthoudt het gesprek.',
  'tour.assistant.p2':
    'Typ of dicteer een vraag in uw eigen woorden — gerust meerdere wijzigingen tegelijk — en dan **Verzenden**.',
  'tour.assistant.p3':
    'Elk antwoord toont de voorgestelde wijzigingen en wat de AREI-controle ervan zou zeggen. **Toepassen** voert ze in één keer uit, als één stap om ongedaan te maken.',
  'tour.assistant.tip':
    'Lees het voorstel vóór u het toepast en controleer het resultaat op het blad: de assistent maakt een ontwerp, u tekent af. Eén duidelijke vraag tegelijk geeft de beste resultaten.',
  'tour.checks.title': 'De AREI-controle',
  'tour.checks.p1':
    'Het paneel **AREI-controle** toont fouten, waarschuwingen en opmerkingen, bijgewerkt bij elke wijziging.',
  'tour.checks.p2':
    'Elk ervan noemt de regel en zegt waarom en hoe te verhelpen; klik erop om te selecteren waarover het gaat. Regels met **te bevestigen** komen uit secundaire bronnen.',
  'tour.checks.p3': 'De teller in de statusbalk blijft altijd zichtbaar.',
  'tour.checks.tip':
    'Hou de teller in het oog terwijl u tekent en los problemen meteen op; mik op nul fouten vóór u exporteert. De controle is een hulp — het erkend organisme beslist.',

  'tour.multiline.title': 'Het meerdraadsschema maken',
  'tour.multiline.p1':
    '**Schema → Meerdraadsschema maken** bouwt het uit het eendraadschema op een nieuw blad: aanvoerklemmen X0, hoofdschakelaar, differentieelschakelaars, rails, automaten en vertrekklemmen X1.',
  'tour.multiline.p2': 'Wijzigt het eendraadschema, dan biedt deze balk **Bijwerken** aan.',
  'tour.multiline.p3':
    'Hetzelfde menu nummert draden en klemmen, kent catalogusreferenties toe en maakt de bordindeling.',
  'tour.multiline.tip':
    'Teken nooit opnieuw wat Tablo kan maken. Wijzig het eendraadschema en klik op **Bijwerken**: handmatige aanpassingen op een gemaakt blad worden vervangen.',

  'tour.sheets.title': 'Eén project, meerdere bladen',
  'tour.sheets.p1':
    'De **+** na de tabs voegt een blad toe: eendraadschema, meerdraadsschema, **stuurschema** (spoelen en contacten verbonden door hun referentie) of **situatieschema** (importeer het plan als PDF, afbeelding of DXF, stel de schaal in, teken ruimtes met **P**, plaats de punten).',
  'tour.sheets.p2': 'Klik op een tab om van blad te wisselen; dubbelklik om hem te hernoemen.',
  'tour.sheets.tip':
    'Eén installatie, één project. Plaats op het situatieschema de punten uit de lijst **Te plaatsen** in plaats van uit het palet: ze houden de code van hun kring, zodat plan en schema overeenkomen zoals het AREI vraagt.',

  'tour.save.title': 'Opslaan en overdragen',
  'tour.save.p1': '**{mod}S** slaat een **.tablo**-bestand op met alles erin, plannen en foto’s inbegrepen.',
  'tour.save.p2':
    '**Revisies** legt het project vast als A, B… met een omschrijving, getoond in het titelvak; **Vergelijken** toont wat er veranderde.',
  'tour.save.p3':
    '**Exporteren — PDF + kringenlijst** maakt het volledige dossier; offerte, etiketten en rapporten staan in hetzelfde menu.',
  'tour.save.p4':
    '**Aanmelden** om uw toestellen te synchroniseren, in team te werken en een leeslink te delen.',
  'tour.save.tip':
    'Automatisch opslaan dient alleen om na een crash te herstellen; het is geen opslag. Sla het bestand op of zet het project in de cloud, en leg een revisie vast telkens een schema uit handen gaat: zo weet u altijd wat er sindsdien veranderde.',

  'tour.done.title': 'U bent klaar',
  'tour.done.body':
    'U startte een project, tekende een bord met de hand en met de kringentabel, las de berekeningen en de controle, en maakte het meerdraadsschema. De gewoontes die lonen:\n- vertrek van een sjabloon of een opname;\n- vul eerst de projectgegevens in;\n- laat draden eindigen op aansluitpunten en vul echte waarden in;\n- bouw de kringen in de tabel en hernummer vóór de overdracht;\n- laat maken in plaats van hertekenen, en klik op Bijwerken;\n- sla het bestand op en leg een revisie vast bij elke uitgave.',
  'tour.done.tip':
    'Open **Bestand → Voorbeeld** voor een volledige woning, of **Bestand → Nieuw vanuit een sjabloon** voor uw eerste echte opdracht. Na de werken neemt het menu **Keuring** de meetresultaten, het keuringsverslag en de factuur. Deze rondleiding blijft in **Bestand → Rondleiding**.',
  'checks.goBlankingOk': 'Afdekplaatjes geplaatst',
  'checks.goBalance': 'Fasen verdelen',
  'checks.balanceNone': 'De fasen zijn al zo goed mogelijk verdeeld.',
  'checks.balanced': '{n} kring(en) naar een andere fase verplaatst. Werk het meerdraadsschema bij.',
  'checks.goAddNorth': 'Noordpijl toevoegen',
  'checks.goDrawRoutes': 'Kabelwegen tekenen',
  'checks.routesNone': 'Niets te tekenen: elke kring op het plan heeft al zijn kabelweg.',
  'checks.routesDrawn':
    '{n} kabelstuk(ken) langs de kortste weg getekend: verplaats ze naar het werkelijke tracé.',
  'survey.blanking': 'Afdekplaatjes',
  'survey.blankingOk': 'Alle geplaatst',
  'survey.blankingMissing': 'Ontbreken',
  'survey.blankingHint': 'Zijn de vrije plaatsen van het bord afgesloten? (AREI art. 49)',
  'checks.report': 'Een fout in deze regel melden',
  'checks.reportTitle': 'Wat klopt er niet aan {rule}?',
  'checks.reportWrong': 'De regel is fout',
  'checks.reportNotApplicable': 'Ze is hier niet van toepassing',
  'checks.reportWording': 'De tekst is onduidelijk',
  'checks.reportOther': 'Andere',
  'checks.reportComment': 'Wat de norm zegt of wat het keuringsorganisme aanvaardt (facultatief)',
  'checks.reportPrivacy':
    'Naar Tablo gestuurd: de regel, de tekst van de vaststelling en uw opmerking, niet het project.',
  'checks.reportSend': 'Versturen',
  'checks.reportThanks': 'Bedankt: de regel wordt nagekeken met de tekst van de norm.',
  'checks.reportError': 'Versturen lukt nu niet. Probeer later opnieuw.',
  'checks.reportSignIn': 'Een gratis Tablo-account is nodig om een regel te melden.',
  'checks.reportSignInButton': 'Aanmelden',
  'signoff.menu': 'Handtekening van de klant…',
  'signoff.title': 'Handtekening van de klant',
  'signoff.handoverText':
    'De klant bevestigt de installatie “{title}” en haar dossier (index {revision}) ontvangen te hebben.',
  'signoff.surveyText': 'De klant bevestigt de opname van de installatie “{title}” zoals genoteerd.',
  'signoff.name': 'Naam van de ondertekenaar',
  'signoff.note': 'Opmerkingen of voorbehouden (facultatief)',
  'signoff.padLabel': 'Handtekeningzone: teken met de vinger of een pen',
  'signoff.clear': 'Wissen',
  'signoff.save': 'Handtekening opslaan',
  'signoff.saved': 'Handtekening in het project opgeslagen.',
  'signoff.txAdd': 'Handtekening van {name}',
  'signoff.txRemove': 'Een handtekening verwijderen',
  'signoff.remove': 'Verwijderen',
  'signoff.of': 'Handtekening van {name}',
  'signoff.kindHandover': 'overdracht',
  'signoff.kindSurvey': 'opname',
  'signoff.revision': 'index {r}',
  'signoff.pack': 'Overdrachtsdossier (PDF)',
  'signoff.step': 'Handtekening',
  'survey.signoff': 'De klant laten tekenen',
  'survey.notSigned': 'De klant heeft de opname nog niet getekend.',
  'survey.signedBy': 'Getekend door {name} op {date}.',
  'meta.ownerEmail': 'E-mail van de klant',
  'meta.ownerPhone': 'Telefoon van de klant',
  'meta.usage': 'Soort installatie',
  'meta.usageHint':
    'Bepaalt de termijn van de periodieke keuring (AREI: 25 jaar huishoudelijk, 5 jaar niet-huishoudelijk).',
  'meta.usageDomestic': 'Huishoudelijk (woning)',
  'meta.usageNonDomestic': 'Niet-huishoudelijk (tertiair, industrie)',
  'clients.menu': 'Klanten en werven…',
  'clients.title': 'Klanten en werven',
  'clients.loading': 'Online projecten laden…',
  'clients.cloudError': 'Online projecten niet beschikbaar: alleen die van dit toestel staan erbij.',
  'clients.count': '{n} klant(en)',
  'clients.ics': 'Herinneringen naar agenda (.ics)',
  'clients.icsFile': 'Tablo keuringen',
  'clients.icsPeriodic': 'Periodieke keuring',
  'clients.icsReinspection': 'Herkeuring',
  'clients.remindersTitle': 'Keuringen te plannen (6 maanden)',
  'clients.duePeriodic': 'periodieke keuring op {date}',
  'clients.dueReinspection': 'herkeuring vóór {date}',
  'clients.overdue': '({n} d te laat)',
  'clients.inDays': '(over {n} d)',
  'clients.search': 'Zoek een klant, adres of project',
  'clients.noMatch': 'Geen klant gevonden.',
  'clients.empty': 'Nog geen project met een klant: vul Bestand → Projectgegevens in.',
  'clients.noName': '(zonder naam)',
  'clients.noAddress': '(geen adres)',
  'clients.untitled': '(zonder titel)',
  'clients.lastInspection': 'gekeurd op {date}: {result}',
  'clients.result.conform': 'conform',
  'clients.result.not-conform': 'niet conform',
  'clients.result.unknown': 'resultaat ?',
  'clients.noInspection': 'geen keuring genoteerd',
  'clients.openRemarks': '{n} open opmerking(en)',
  'clients.isOpen': 'nu open',
  'clients.inCloud': 'online',
  'clients.onDevice': 'op dit toestel',
  'clients.onDeviceFile': 'bestand “{file}”',
  'clients.open': 'Openen',
  'clients.forget': 'Uit de lijst halen',
  'clients.hint':
    'De lijst bevat de projecten die op dit toestel geopend werden en die van uw online werkruimtes. AREI-termijnen: 25 jaar (huishoudelijk) of 5 jaar (niet-huishoudelijk) na een conforme keuring, 12 maanden om te herstellen na een niet-conforme.',
  'menu.labelsTape': 'Etiketten voor labelprinter — tape {mm} mm (PDF)',
  'report.tapeHint':
    '{n} strook/stroken, één per rij. Druk af op 100 % op tape van {mm} mm (Brother P-touch, Dymo: aangepaste lengte in de driver).',
  'instr.button': 'Importeren uit een tester…',
  'instr.title': 'Resultaten van de tester — {file}',
  'instr.summary': '{n} rij(en) metingen, {m} gekoppeld aan een kring of differentieel',
  'instr.unreadable':
    'Onleesbaar bestand: exporteer als CSV of Excel (.xlsx) vanuit de software van de tester.',
  'instr.record': '{n} resultaat/resultaten opslaan',
  'instr.tx': 'Import uit tester ({n} rijen)',
  'instr.done': 'Metingen geïmporteerd — kringen: {c}; differentiëlen: {r}.',
  'instr.line': 'Rij',
  'instr.goesTo': 'Gaat naar',
  'instr.skip': '(overslaan)',
  'instr.fieldRef': 'Kring / locatie',
  'instr.fieldInsulation': 'Isolatie',
  'instr.fieldTestVoltage': 'Testspanning',
  'instr.fieldContinuity': 'Continuïteit PE',
  'instr.fieldLoop': 'Lus Zs',
  'instr.fieldTripMs': 'Uitschakeltijd',
  'instr.fieldTripMa': 'Uitschakelstroom',
  'instr.fieldDate': 'Datum',
  'instr.hint':
    'Elk merk noemt zijn kolommen anders: kijk de kolom per waarde en de kring van elke rij na. Uitschakeltijden en -stromen op de rij van een kring gaan naar de differentieel die hem beveiligt. Een kring die die dag al gemeten werd, wordt bijgewerkt.',
  'pick.menu': 'Picklijst van het materiaal…',
  'pick.title': 'Picklijst',
  'pick.summary': '{n} artikel(s), {o} te bestellen',
  'pick.take': 'Uit de bestelwagen nemen',
  'pick.taken': 'Voorraad van de bestelwagen bijgewerkt.',
  'pick.excel': 'Bestelling (Excel)',
  'pick.csv': 'Bestelling voor de groothandel (CSV)',
  'pick.file': 'picklijst',
  'pick.order': 'bestelling',
  'pick.ref': 'Referentie',
  'pick.desc': 'Omschrijving',
  'pick.needed': 'Nodig',
  'pick.inVan': 'In de bestelwagen',
  'pick.toOrder': 'Te bestellen',
  'pick.hint':
    'De voorraad van de bestelwagen wordt op dit toestel bewaard en geldt voor al uw werven. Kabels in meter, met afval (10 %). De CSV “referentie;aantal” laat zich importeren in de webshops van de groothandels.',
  'share.copyHome': 'Link voor de bewoner kopiëren',
  'home.title': 'Uw elektrische installatie',
  'home.loading': 'Laden…',
  'home.error': 'Deze link is niet meer geldig. Vraag uw elektricien een nieuwe link.',
  'home.underRcd': 'Onder de differentieel {tag} ({ma} mA)',
  'home.noRcd': 'Kringen',
  'home.circuit': 'Kring',
  'home.sockets': '{n} stopcontact(en)',
  'home.lights': '{n} lichtpunt(en)',
  'home.appliances': '{n} toestel(len)',
  'home.tripTitle': 'Als een differentieel uitschakelt',
  'home.trip1':
    'Trek de stekkers uit van wat net werd ingeplugd en schakel de automaten onder die differentieel uit (zie de lijst hierboven).',
  'home.trip2': 'Zet de differentieel terug aan (hendel omhoog).',
  'home.trip3':
    'Zet de automaten één voor één terug aan. Die waarbij de differentieel opnieuw uitschakelt, is de defecte kring: laat hem uit.',
  'home.trip4':
    'Trek op die kring alles uit en steek de toestellen één voor één terug in om het defecte toestel te vinden.',
  'home.trip5': 'Schakelt hij nog uit met alles uitgetrokken, of gebeurt het vaak, bel dan uw elektricien.',
  'home.test':
    'Druk af en toe op de “T”-knop van elke differentieel: hij moet meteen uitschakelen. Zet hem daarna terug aan.',
  'home.never': 'Forceer nooit een differentieel of automaat die niet aan blijft, en open het bord niet.',
  'home.installer': 'Installateur: {name}',
  'home.drawings': 'De schema’s van de installatie bekijken',
  'energy.menu': 'Energie: capaciteitstarief en netbeheerder…',
  'energy.title': 'Energie',
  'energy.capacityTitle': 'Capaciteitstarief (Vlaanderen)',
  'energy.notFlanders': 'Het capaciteitstarief geldt in Vlaanderen; dit adres ligt er niet.',
  'energy.basePeak': 'Gemiddelde maandpiek nu (kW)',
  'energy.area': 'Fluvius-gebied (2026)',
  'energy.noLoads': 'Geen groot toestel met een opgegeven vermogen (laadpunt, warmtepomp…).',
  'energy.before': 'Nu',
  'energy.after': 'Met de nieuwe toestellen',
  'energy.managed': 'Met lastbeheer (laadpunt op {kw})',
  'energy.year': 'jaar',
  'energy.advice':
    'Lastbeheer (het laadpunt vertraagt tot {kw} terwijl het huis verbruikt) bespaart ongeveer {eur} per jaar aan capaciteitstarief.',
  'energy.hint':
    'Een schatting: de echte piek is het gemiddelde van het hoogste kwartier van elke maand (minstens 2,5 kW), te zien in de gegevens van de digitale meter. Vink de toestellen aan die werken op het moment van de piek van het huis.',
  'energy.gridTitle': 'Netbeheerder',
  'energy.operator': 'Netbeheerder van dit adres: {op}.',
  'energy.operatorCheck': 'Waarschijnlijke netbeheerder: {op} (na te kijken: hangt af van de gemeente).',
  'energy.noPostcode': 'Geen postcode in het adres: kies de netbeheerder.',
  'energy.nothingToTell': 'Het schema heeft geen zonnepanelen, omvormer, laadpunt of warmtepomp.',
  'energy.operatorLabel': 'Netbeheerder',
  'energy.inverter': 'Omvormer (merk, model)',
  'energy.batteryKwh': 'Batterij (kWh)',
  'energy.batteryKw': 'Batterij (kW)',
  'energy.date': 'Indienststelling',
  'energy.ev': 'Laadpunt',
  'energy.heatPump': 'Warmtepomp',
  'energy.pdf': 'Gegevens voor {op} (PDF)',
  'teamleader.connect': 'Teamleader koppelen…',
  'teamleader.send': 'Naar Teamleader sturen',
  'teamleader.sent': 'Offerte naar Teamleader gestuurd: een deal en een offerte bij de bestaande klant.',
  'teamleader.sentNew': 'Offerte naar Teamleader gestuurd: klant toegevoegd, met een deal en een offerte.',
  'teamleader.error': 'Teamleader: {msg}',
  'teamleader.connected': 'Teamleader is gekoppeld: stuur uw offertes vanuit de offerte.',
  'teamleader.failed': 'Koppelen met Teamleader is mislukt. Probeer opnieuw.',
  'teamleader.material': 'Materiaal',
  'teamleader.cables': 'Kabels',
  'teamleader.labour': 'Arbeid',
  'remedy.button': 'Offerte conform maken…',
  'remedy.title': 'Offerte voor het conform maken',
  'remedy.intro':
    'Elke open opmerking van het verslag wordt offertelijnen: het gebruikelijke werk, het materiaal en de uren. Pas het werk, het aantal en de uren aan en exporteer of factureer.',
  'remedy.remark': 'Opmerking',
  'remedy.fix': 'Werk',
  'remedy.qty': 'Aantal',
  'remedy.hours': 'Uren',
  'remedy.include': 'Opmerking {n} opnemen',
  'remedy.none': 'Geen open opmerkingen in dit verslag: niets te begroten.',
  'remedy.unpriced':
    '{n} lijn(en) zonder prijs (afdekplaatjes, aardpen…): aan te vullen in het Excel-bestand of in uw prijslijst.',
  'remedy.file': 'offerte conform maken',
  'scan.title': 'Scan van de ruimtes',
  'scan.summary': '{walls} muren, {doors} deuren, {windows} ramen, {rooms} ruimte(s) herkend — {w} × {h} m.',
  'scan.story': 'Verdieping',
  'scan.storyN': 'Niveau {n}',
  'scan.objects': 'Herkende meubels en toestellen tekenen ({n})',
  'scan.note':
    'Het plan is op ware schaal. Muren worden op hun middellijn getekend, want de scan meet hun dikte niet: controleer belangrijke maten ter plaatse.',
  'scan.start': 'Ruimtes scannen (LiDAR)…',
  'scan.failed': 'Scannen mislukt: {msg}',
  'scan.notScan': 'Dit JSON-bestand is geen ruimtescan (RoomPlan).',
  'offer.send': 'Online ter aanvaarding versturen…',
  'offer.title': 'Online aanvaarding',
  'offer.intro':
    'De klant krijgt een link: hij leest de offerte, ondertekent met de vinger of de muis, aanvaardt en ziet dan hoe hij het voorschot betaalt. U wordt per e-mail verwittigd.',
  'offer.docTitle': 'Titel van de offerte',
  'offer.number': 'Nummer',
  'offer.valid': 'Geldig (dagen)',
  'offer.deposit': 'Voorschot bij aanvaarding (%)',
  'offer.note': 'Voorwaarden en bericht aan de klant',
  'offer.noIban':
    'Zonder IBAN in uw factuurgegevens (Controle → Facturen) ziet de klant geen QR-code om het voorschot te betalen.',
  'offer.unpriced': '{n} lijn(en) zonder prijs: vul de prijzen in voor u de offerte verstuurt.',
  'offer.create': 'Link aanmaken',
  'offer.link': 'Link voor de klant',
  'offer.copy': 'Link kopiëren',
  'offer.copied': 'Link gekopieerd.',
  'offer.email': 'Per e-mail versturen…',
  'offer.mailSubject': 'Offerte: {title}',
  'offer.mailBody':
    'Goeddag,\n\nHierbij onze offerte „{title}”. U kunt ze online lezen en aanvaarden:\n{url}\n\nMet vriendelijke groeten,\n{seller}',
  'offer.sent': 'Verstuurd ter aanvaarding',
  'offer.none': 'Nog geen offerte online verstuurd voor dit project.',
  'offer.statusOpen': 'In afwachting, geldig tot {date}',
  'offer.statusAccepted': 'Aanvaard door {name} op {date}',
  'offer.statusWithdrawn': 'Ingetrokken',
  'offer.statusExpired': 'Verlopen op {date}',
  'offer.withdraw': 'Intrekken',
  'offer.signIn': 'Meld u aan om een offerte online ter aanvaarding te versturen.',
  'offer.failed': 'Versturen mislukt: {msg}',
  'offer.signature': 'Handtekening van {name}',
  'offerPage.loading': 'Offerte laden…',
  'offerPage.error': 'Deze link werkt niet meer. Neem contact op met uw installateur.',
  'offerPage.from': 'Offerte van {seller}',
  'offerPage.for': 'Voor',
  'offerPage.site': 'Werf',
  'offerPage.dates': 'Van {date} · geldig tot {until}',
  'offerPage.colDesc': 'Omschrijving',
  'offerPage.colQty': 'Aantal',
  'offerPage.colPrice': 'EP excl. btw',
  'offerPage.colTotal': 'Totaal excl. btw',
  'offerPage.discount': '{pct} % korting op materiaal en kabels.',
  'offerPage.vat': 'Btw {pct} %',
  'offerPage.subtotal': 'Totaal excl. btw',
  'offerPage.total': 'Totaal incl. btw',
  'offerPage.acceptTitle': 'De offerte aanvaarden',
  'offerPage.depositAsk': 'Bij aanvaarding wordt een voorschot van {pct} % ({amount}) gevraagd.',
  'offerPage.name': 'Uw naam',
  'offerPage.sign': 'Uw handtekening',
  'offerPage.agree': 'Ik heb deze offerte gelezen en aanvaard ze, met haar voorwaarden.',
  'offerPage.accept': 'Aanvaarden en ondertekenen',
  'offerPage.sending': 'Versturen…',
  'offerPage.thanks': 'Bedankt, de offerte is aanvaard. {seller} is verwittigd.',
  'offerPage.accepted': 'Offerte aanvaard door {name} op {date}.',
  'offerPage.pay': 'Het voorschot betalen: {amount}',
  'offerPage.payHow':
    'Scan de code met uw bankapp, of schrijf {amount} over op {iban} ({name}) met de mededeling {ref}.',
  'offerPage.expired': 'Deze offerte is verlopen op {date}. Vraag uw installateur een nieuwe offerte.',
  'offerPage.failed': 'De aanvaarding is niet gelukt: {msg}',
  'offerPage.contact': 'Een vraag? Neem contact op met {seller}',
  'offerPage.privacy': 'Uw naam en handtekening worden bij de offerte bewaard voor de installateur.',
  'rev.between': 'Twee toestanden vergelijken',
  'rev.from': 'Van',
  'rev.to': 'Tot',
  'rev.now': 'Nu',
  'rev.show': 'Tonen op de schema’s',
  'rev.pdf': 'PDF van de wijzigingen',
  'rev.diffBetween':
    'Revisie {from} → {to}: {added} toegevoegd, {changed} gewijzigd, {removed} verwijderd — in groen, oranje en rood gestreept. Alleen lezen.',
  'rev.list': 'Lijst van wijzigingen',
  'rev.listTitle': 'Wijzigingen van {from} tot {to}',
  'rev.noChange': 'Geen wijzigingen aan de schema’s.',
  'rev.kindAdded': 'Toegevoegd',
  'rev.kindChanged': 'Gewijzigd',
  'rev.kindRemoved': 'Verwijderd',
  'rev.wires': 'Draden: {added} toegevoegd, {removed} verwijderd, {changed} gewijzigd',
  'rev.file': 'wijzigingen',
  'inspection.since': 'Wat veranderde sinds deze keuring (revisie {index})',
  'inspection.noRevision':
    'Maak een revisie voor de keuring (Bestand → Revisies) om later te zien wat sindsdien veranderde.',
  'share.createReview': 'Link voor het keuringsorganisme',
  'share.reviewHint':
    'De keurder ziet de schema’s alleen-lezen en prikt zijn opmerkingen er rechtstreeks op; u neemt ze over in Controle → Keuringsverslagen.',
  'share.review': 'Keuringsorganisme',
  'review.banner': 'Online keuring: {n} opmerking(en) geprikt.',
  'review.pin': 'Opmerking prikken',
  'review.picking': 'Klik op het schema waar de opmerking over gaat (Esc om te annuleren)',
  'review.list': 'Opmerkingen',
  'review.title': 'Opmerking nr. {n}',
  'review.author': 'Keurder',
  'review.body': 'Organisme',
  'review.text': 'Opmerking',
  'review.article': 'Artikel',
  'review.ref': 'Kring of punt',
  'review.save': 'Prikken',
  'review.delete': 'Verwijderen',
  'review.taken': 'overgenomen door de installateur',
  'review.none': 'Geen opmerkingen geprikt.',
  'review.failed': 'Opmerking niet bewaard: {msg}',
  'review.sheet': 'Blad {name}',
  'inspection.pins': '{n} opmerking(en) online geprikt door het organisme',
  'inspection.importPins': 'Overnemen als verslag',
  'inspection.pinsReference': 'Online keuring',
  'inspection.txPins': 'Online opmerkingen overnemen',
  'inspection.showPin': 'Tonen op het schema',
  'cat.generation': 'PV-productie en batterij',
  'pv.menu': 'Fotovoltaïsche installatie…',
  'pv.title': 'Fotovoltaïsche installatie',
  'pv.intro':
    'Voegt aan het bord een eigen kring voor de omvormer toe, onder een eigen differentieel, en tekent de DC-zijde op een nieuw blad: strings, DC-scheiders, overspanningsbeveiliging, omvormer en batterij.',
  'pv.board': 'Bord',
  'pv.strings': 'Strings van panelen',
  'pv.string': 'String {n}',
  'pv.modules': 'Panelen',
  'pv.moduleWp': 'Wp per paneel',
  'pv.vocV': 'Voc string (V)',
  'pv.addString': '+ String',
  'pv.removeString': 'String {n} verwijderen',
  'pv.inverterKw': 'AC-vermogen van de omvormer (kW)',
  'pv.phases': 'Aansluiting',
  'pv.mono': 'Eenfasig',
  'pv.tri': 'Driefasig',
  'pv.battery': 'Batterij (kWh, 0 = geen)',
  'pv.rcdType': 'Differentieel van de omvormer',
  'pv.typeB': 'Type B',
  'pv.typeA': 'Type A (als de handleiding van de omvormer het toelaat)',
  'pv.sensitivity': 'Gevoeligheid',
  'pv.dcSpd': 'Overspanningsbeveiliging aan DC-zijde',
  'pv.summary': '{kwp} kWp · omvormer {kw} kW: automaat {rating} A, kabel {section} mm²',
  'pv.create': 'Installatie tekenen',
  'pv.noBoard': 'Teken eerst het bord (eendraadsschema met de netaansluiting).',
  'pv.sheetName': 'PV-installatie — DC-zijde',
  'pv.circuitLabel': 'PV-omvormer',
  'pv.tx': 'Fotovoltaïsche installatie',
  'pv.labelsMenu': 'Etiketten van de PV-installatie (PDF)',
  'pv.labelsFile': 'PV-etiketten',
  'pv.noLabels': 'Geen PV-installatie in het project.',
  'sheet.tabs': 'Bladen van het project',
  'sheet.menu': 'Blad “{name}”: opties',
  'settings.menu': 'Instellingen…',
  'settings.title': 'Instellingen',
  'settings.usage': 'Anonieme gebruiksstatistieken delen',
  'settings.usageHint':
    'Hoe vaak elke opdracht gebruikt wordt, per dag, met de versie en de taal van de app. Geen account, geen adres, geen inhoud van projecten. Zo weten we wat te verbeteren. Standaard uit.',
  'settings.privacy': 'Privacybeleid',
  'backup.title': 'Automatische back-up',
  'backup.enable': 'Het open project automatisch back-uppen',
  'backup.hint':
    'Een .tablo-kopie wordt in de gekozen map geschreven wanneer het project gewijzigd is. De kopieën blijven op dit toestel.',
  'backup.folder': 'Map',
  'backup.noFolder': 'geen',
  'backup.choose': 'Kiezen…',
  'backup.needsPermission': 'De browser vraagt toestemming voor hij opnieuw in deze map schrijft.',
  'backup.resume': 'Toestaan',
  'backup.every': 'Elke',
  'backup.keep': 'Bewaarde kopieën per project',
  'backup.now': 'Nu back-uppen',
  'backup.last': 'Laatste kopie om {time}: {name}',
};
